import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import PermissionsTab from '../components/admin/PermissionsTab';
import CreateStartupModal from '../components/CreateStartupModal';
import FilePreviewModal from '../components/FilePreviewModal';
import FileUpload from '../components/FileUpload';
import CustomDialog from '../components/CustomDialog';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import DetailViewNavigation from '../components/DetailViewNavigation';
import InvestmentTabbedDetails from '../components/investments/InvestmentTabbedDetails';
import Logo from '../components/Logo';
import { adminAPI, investorAPI, investmentRoundAPI, fileAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/api.config';

const startupValidationBadgeClass = (status) => {
	if (status === 'VALIDATED') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
	if (status === 'REJECTED') return 'bg-red-100 text-red-800 border-red-200';
	return 'bg-amber-100 text-amber-800 border-amber-200';
};

const startupValidationLabel = (status) => {
	if (!status || status === 'PENDING') return 'Pending review';
	const map = { VALIDATED: 'Validated', REJECTED: 'Rejected' };
	return map[status] || String(status).replace(/_/g, ' ');
};

const STAGES = ['Idea', 'MVP', 'Pre-Seed', 'Seed', 'Series A', 'Series B', 'Growth', 'Exit'];

// Helper functions for document type/category
const getCategoryLabel = (category) => {
    if (!category) return 'General';
    const categoryMap = {
        'PITCH_DECK': 'Pitch Deck',
        'INVESTOR_PRESENTATION': 'Investor Presentation',
        'EXECUTIVE_SUMMARY': 'Executive Summary',
        'FINANCIAL': 'Financial Statements',
        'AUDITED_FINANCIALS': 'Audited Financials',
        'PROJECTED_FINANCIALS': 'Projected Financials',
        'BALANCE_SHEET': 'Balance Sheet',
        'P_L_STATEMENT': 'P&L Statement',
        'CASH_FLOW_STATEMENT': 'Cash Flow Statement',
        'VALUATION_CERTIFICATE': 'Valuation Certificate',
        'CAP_TABLE': 'Cap Table',
        'LEGAL': 'Legal Documents',
        'INCORPORATION': 'Incorporation Documents',
        'AGREEMENT': 'Agreements',
        'SHA': 'Shareholder Agreement',
        'TERM_SHEET': 'Term Sheet',
        'CCD': 'CCD',
        'SAFE_AGREEMENT': 'SAFE Agreement',
        'CONVERTIBLE_NOTE': 'Convertible Note',
        'EMPLOYMENT_AGREEMENT': 'Employment Agreement',
        'NDA': 'NDA',
        'IP_DOCUMENTS': 'IP Documents',
        'PATENTS': 'Patents',
        'TRADEMARKS': 'Trademarks',
        'BUSINESS_PLAN': 'Business Plan',
        'MARKET_RESEARCH': 'Market Research',
        'COMPETITIVE_ANALYSIS': 'Competitive Analysis',
        'BUSINESS_MODEL': 'Business Model',
        'GO_TO_MARKET': 'Go-to-Market',
        'PRODUCT_DOCUMENTATION': 'Product Docs',
        'TECHNICAL_SPECS': 'Technical Specs',
        'COMPLIANCE': 'Compliance',
        'LICENSES': 'Licenses',
        'REGULATORY_FILINGS': 'Regulatory Filings',
        'TAX_DOCUMENTS': 'Tax Documents',
        'OPERATIONAL': 'Operational',
        'HR_DOCUMENTS': 'HR Documents',
        'POLICIES': 'Policies',
        'PROCESSES': 'Processes',
        'MARKETING_MATERIALS': 'Marketing',
        'SALES_DECK': 'Sales Deck',
        'BRAND_GUIDELINES': 'Brand Guidelines',
        'GENERAL': 'General',
        'OTHER': 'Other'
    };
    return categoryMap[category] || category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getCategoryColor = (category) => {
    if (!category) return 'bg-gray-100 text-gray-800';
    if (category.includes('PITCH') || category.includes('PRESENTATION') || category.includes('DECK')) {
        return 'bg-purple-100 text-purple-800';
    } else if (category.includes('FINANCIAL') || category.includes('VALUATION') || category.includes('CAP_TABLE') || category.includes('BALANCE') || category.includes('CASH_FLOW') || category.includes('P_L')) {
        return 'bg-green-100 text-green-800';
    } else if (category.includes('LEGAL') || category.includes('AGREEMENT') || category.includes('SHA') || category.includes('TERM') || category.includes('NDA') || category.includes('PATENT') || category.includes('TRADEMARK') || category.includes('IP')) {
        return 'bg-blue-100 text-blue-800';
    } else if (category.includes('BUSINESS') || category.includes('MARKET') || category.includes('COMPETITIVE') || category.includes('PRODUCT') || category.includes('TECHNICAL')) {
        return 'bg-orange-100 text-orange-800';
    } else if (category.includes('COMPLIANCE') || category.includes('LICENSE') || category.includes('REGULATORY') || category.includes('TAX')) {
        return 'bg-yellow-100 text-yellow-800';
    } else if (category.includes('OPERATIONAL') || category.includes('HR') || category.includes('POLICY') || category.includes('PROCESS')) {
        return 'bg-indigo-100 text-indigo-800';
    } else if (category.includes('MARKETING') || category.includes('SALES') || category.includes('BRAND')) {
        return 'bg-pink-100 text-pink-800';
    } else {
        return 'bg-gray-100 text-gray-800';
    }
};

const StartupStageTimeline = ({ currentStage, onStageSelect }) => {
    const currentIndex = STAGES.findIndex(s => (s || '').toLowerCase() === (currentStage || '').toLowerCase());
    const activeIndex = currentIndex === -1 ? 0 : currentIndex;

    return (
        <div className="w-full py-2 px-2 overflow-x-auto bg-white border-b border-gray-100">
            <div className="relative flex items-center justify-between min-w-[500px] h-12">
                {/* Connecting Lines */}
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-[2px] bg-gray-100 z-0 rounded-full" />
                <div
                    className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-blue-500 z-0 rounded-full transition-all duration-500"
                    style={{ width: `${(activeIndex / (STAGES.length - 1)) * 100}%` }}
                />

                {STAGES.map((stage, index) => {
                    const isActive = index === activeIndex;
                    const isPassed = index < activeIndex;

                    return (
                        <div
                            key={stage}
                            className="relative flex flex-col items-center group z-10 cursor-pointer"
                            onClick={() => onStageSelect && onStageSelect(stage)}
                        >
                            {isActive ? (
                                <div className="relative">
                                    <div className="bg-blue-500 text-white px-2 py-0.5 rounded-full flex items-center space-x-1 shadow-sm border border-white box-content transform transition-transform duration-200 hover:scale-105">
                                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span className="font-semibold text-[10px] whitespace-nowrap leading-none pb-0.5">{stage}</span>
                                    </div>
                                </div>
                            ) : (
                                <div className={`w-2 h-2 rounded-full border box-content transition-all duration-300 ${isPassed ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white hover:border-blue-400'
                                    }`}>
                                    {/* Hover tooltip for non-active stages */}
                                    <div className="absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 px-1.5 py-0.5 bg-gray-800 text-white text-[9px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                                        {stage}
                                    </div>
                                </div>
                            )}

                            {/* Decision Icons for final stage - scaled down significantly */}
                            {index === STAGES.length - 1 && (
                                <div className="absolute top-4 flex space-x-1 opacity-20 hover:opacity-100 transition-opacity">
                                    <div className="bg-green-50 rounded-full border border-green-200">
                                        <svg className="w-2 h-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" /></svg>
                                    </div>
                                    <div className="bg-red-50 rounded-full border border-red-200">
                                        <svg className="w-2 h-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.095c.5 0 .905-.405.905-.905 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" /></svg>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const StartupDetail = ({ accessScope = 'full' }) => {
    const { tenantId, id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isPortfolioView = accessScope === 'portfolio';
    const canEditStartupDetails = ['ADMIN', 'TENANT_ADMIN', 'STARTUP', 'ENTREPRENEUR'].includes(user?.role);
    const canEditStartupValidation = ['ADMIN', 'TENANT_ADMIN'].includes(user?.role);
    /** Directors / cap table–style personnel: only startup account + admins (not investors / networks). */
    const canManageStartupDirectors = canEditStartupDetails && !isPortfolioView;
    const canManageTranchesBase = ['STARTUP', 'ENTREPRENEUR', 'ADMIN', 'TENANT_ADMIN', 'INVESTOR_NETWORK', 'MANAGEMENT'].includes(
        user?.role
    );
    /** New tranche, edit round, add commitment — same roles on full startup detail and portfolio (investor-network) startup page */
    const canManageTrancheStructure = canManageTranchesBase;
    const canAddMilestone = ['INVESTOR', 'INVESTOR_NETWORK'].includes(user?.role);
    const canUpdateMilestoneStatus = ['STARTUP', 'ENTREPRENEUR'].includes(user?.role);
    const [searchParams, setSearchParams] = useSearchParams();
    const [startup, setStartup] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
    const [files, setFiles] = useState([]);
    const [directors, setDirectors] = useState([]);
    const [stageHistory, setStageHistory] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAddDirectorModal, setShowAddDirectorModal] = useState(false);
    const [showEditDirectorModal, setShowEditDirectorModal] = useState(false);
    const [editingDirector, setEditingDirector] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPermissionsModal, setShowPermissionsModal] = useState(false);
    const [selectedFileForPermissions, setSelectedFileForPermissions] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [investors, setInvestors] = useState([]);
    const [investments, setInvestments] = useState([]);
    const [newDirector, setNewDirector] = useState({ name: '', email: '', phone: '', role: '', category: '', shareholding: '', aadharNumber: '', panNumber: '' });
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showDeleteDirectorConfirm, setShowDeleteDirectorConfirm] = useState(false);
    const [directorToDelete, setDirectorToDelete] = useState(null);
    const [selectedDocumentType, setSelectedDocumentType] = useState('ALL');
    const [editingFundingId, setEditingFundingId] = useState(null);
    const [editingFundingAmount, setEditingFundingAmount] = useState('');
    const [updatingFunding, setUpdatingFunding] = useState(false);
    const [selectedInvestment, setSelectedInvestment] = useState(null);
    const [showInvestmentDetailsModal, setShowInvestmentDetailsModal] = useState(false);
    const [showTotalInvestmentModal, setShowTotalInvestmentModal] = useState(false);
    const [totalInvestmentAmountInput, setTotalInvestmentAmountInput] = useState('');
    /** Per-investment row in "Edit Total Investment & Split" modal: amount + ledger fields */
    const [splitLedgerDraft, setSplitLedgerDraft] = useState({});
    const splitLedgerInitialRef = useRef({});
    const [savingTotalInvestmentSplit, setSavingTotalInvestmentSplit] = useState(false);
    const [investmentLedgerSearch, setInvestmentLedgerSearch] = useState('');
    const [editingCommitmentId, setEditingCommitmentId] = useState(null);
    const [editingCommittedAmount, setEditingCommittedAmount] = useState('');
    const [editingCommitmentMeta, setEditingCommitmentMeta] = useState({
        commitmentDate: '',
        commitmentCurrency: 'INR',
        conditions: ''
    });
    const [updatingCommitmentAmount, setUpdatingCommitmentAmount] = useState(false);
    const [editData, setEditData] = useState({
        companyName: '',
        industry: '',
        stage: '',
        description: '',
        website: '',
        phoneNumber: ''
    });
    const [validationForm, setValidationForm] = useState({ status: 'PENDING', notes: '' });
    const [savingValidation, setSavingValidation] = useState(false);

    // Investment Tranches state
    const [investmentSubTab, setInvestmentSubTab] = useState('investments');
    const [rounds, setRounds] = useState([]);
    const [networks, setNetworks] = useState([]);
    const [allInvestors, setAllInvestors] = useState([]);
    // Network investors (for INVESTOR_NETWORK add-investment form)
    const [networkInvestors, setNetworkInvestors] = useState([]);
    const [sidebarExpanded, setSidebarExpanded] = useState(false);
    const [selectedRound, setSelectedRound] = useState(null);
    const [selectedCommitment, setSelectedCommitment] = useState(null);
    const [showRoundModal, setShowRoundModal] = useState(false);
    const [showCommitmentModal, setShowCommitmentModal] = useState(false);
    const [showParticipationModal, setShowParticipationModal] = useState(false);
    const [showTermModal, setShowTermModal] = useState(false);
    const [showMilestoneTargetModal, setShowMilestoneTargetModal] = useState(false);
    const [milestoneTargetCommitmentId, setMilestoneTargetCommitmentId] = useState('');
    const [termCommitmentIdOverride, setTermCommitmentIdOverride] = useState(null);
    const [editingRound, setEditingRound] = useState(null);
    const [roundForm, setRoundForm] = useState({
        roundName: 'Seed',
        targetAmount: '',
        sharePrice: '',
        preMoneyValuation: '',
        postMoneyValuation: '',
        totalShares: '',
        openDate: new Date().toISOString().split('T')[0],
        closeDate: '',
        notes: ''
    });
    // Optional: record network + network investors for this round (INVESTOR_NETWORK only)
    const [roundInvestments, setRoundInvestments] = useState({
        networkAmount: '',
        investorAmounts: {} // { [investorId]: amount string }
    });
    const [commitmentForm, setCommitmentForm] = useState({
        networkId: '',
        committedAmount: '',
        equityPercentage: '',
        numberOfShares: '',
        investmentType: 'EQUITY',
        notes: '',
        networkInvestmentAmount: '',
        selectedMemberIds: [],
        commitmentDate: '',
        commitmentCurrency: 'INR',
        conditions: ''
    });
    const [networkMembers, setNetworkMembers] = useState([]);
    const [participationForm, setParticipationForm] = useState({
        investorId: '',
        amount: '',
        participationPercentage: '',
        sharesAllocated: '',
        notes: ''
    });
    const [termForm, setTermForm] = useState({
        amount: '',
        percentage: '',
        dueDate: '',
        releaseDate: '',
        milestone: '',
        notes: ''
    });
    const [confirm, setConfirm] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'danger',
        onConfirm: null
    });
    const [investorNetworkIdFromMe, setInvestorNetworkIdFromMe] = useState(null);

    // Fetch /users/me for investorNetworkId when viewing as INVESTOR (for sidebar Network link)
    useEffect(() => {
        if (user?.role !== 'INVESTOR') return;
        const token = localStorage.getItem('token');
        if (!token) return;
        fetch(getApiUrl('users/me'), { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
                if (data?.investorNetworkId != null) setInvestorNetworkIdFromMe(data.investorNetworkId);
            })
            .catch(() => {});
    }, [user?.role]);

    const effectiveInvestorNetworkId = user?.investorNetworkId ?? investorNetworkIdFromMe;
    const canEditNetworkTotalInvestment = ['INVESTOR_NETWORK', 'MANAGEMENT'].includes(user?.role);
    const allTranches = Array.isArray(rounds) ? rounds : [];
    const startupTotalInvestmentAmount = allTranches.reduce((sum, tranche) => sum + Number(tranche?.raisedAmount || 0), 0);
    const selectedTrancheTotalCommitted = (selectedRound?.commitments || []).reduce(
        (sum, commitment) => sum + Number(commitment?.committedAmount || 0),
        0
    );

    // Sync query param to activeTab when URL changes
    useEffect(() => {
        const tabFromUrl = searchParams.get('tab');
        if (tabFromUrl && ['overview', 'files', 'investments', 'milestones', 'stage-history', 'founder', 'directors'].includes(tabFromUrl)) {
            // Support both 'founder' and 'directors' for backward compatibility - map 'founder' to 'directors'
            setActiveTab(tabFromUrl === 'founder' ? 'directors' : tabFromUrl);
        }
    }, [searchParams]);

    // Update URL when tab changes
    useEffect(() => {
        setSearchParams({ tab: activeTab });
    }, [activeTab, setSearchParams]);

    useEffect(() => {
        if (!id || !user?.role) return;
        // Load all data in parallel for faster page load
        Promise.all([
            loadStartupDetails(),
            loadFiles(),
            loadDirectors(),
            loadStageHistory(),
            loadInvestors(),
            loadInvestments(),
            loadInvestmentRounds(),
            loadNetworksAndInvestors()
        ]);
    }, [id, user?.role, user?.id, accessScope]);

    // Full startup detail for networks defaults to tranche management. Portfolio view keeps default
    // `investments` sub-tab (ledger) — do not override when user switches to "Investment tranches".
    useEffect(() => {
        if (isPortfolioView) return;
        if (user?.role === 'INVESTOR_NETWORK' || user?.role === 'MANAGEMENT') {
            setInvestmentSubTab('tranches');
        }
    }, [user?.role, isPortfolioView]);

    // Deep-link: ?tab=investments should open main tab + investments ledger sub-tab on portfolio view
    useEffect(() => {
        if (!isPortfolioView) return;
        const t = searchParams.get('tab');
        if (t === 'investments') setInvestmentSubTab('investments');
    }, [isPortfolioView, searchParams]);

    useEffect(() => {
        if (user?.role !== 'INVESTOR_NETWORK' && user?.role !== 'MANAGEMENT') return;
        const loadNetworkInvestors = async () => {
            try {
                const response = await fetch(getApiUrl('users/my-network/investors'), {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setNetworkInvestors(Array.isArray(data) ? data : []);
                }
            } catch (e) {
                console.error('Failed to load network investors:', e);
            }
        };
        loadNetworkInvestors();
    }, [user?.role, id]);

    const loadStartupDetails = async () => {
        setLoading(true);
        try {
            let data;
            if (user?.role === 'INVESTOR' || user?.role === 'INVESTOR_NETWORK' || user?.role === 'MANAGEMENT') {
                const response = await fetch(getApiUrl(`investments/startup/${id}/profile`), {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (response.ok) {
                    data = await response.json();
                } else if (user?.role === 'INVESTOR' && user?.id) {
                    // Fallback for investor: derive startup info from their accessible portfolio.
                    const portfolioRes = await fetch(getApiUrl(`investments/investor/${user.id}/portfolio`), {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    if (!portfolioRes.ok) {
                        setStartup(null);
                        return;
                    }
                    const portfolio = await portfolioRes.json();
                    const matched = Array.isArray(portfolio)
                        ? portfolio.find((s) => String(s?.id) === String(id))
                        : null;
                    if (!matched) {
                        setStartup(null);
                        return;
                    }
                    data = matched;
                } else {
                    setStartup(null);
                    return;
                }
            } else {
                const response = await fetch(getApiUrl(`admin/users/${id}`), {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (!response.ok) {
                    setStartup(null);
                    return;
                }
                data = await response.json();
            }
            setStartup(data);
        } catch (error) {
            console.error('Failed to load startup details:', error);
            setStartup(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!startup) return;
        setValidationForm({
            status: startup.validationStatus || 'PENDING',
            notes: startup.validationNotes || ''
        });
    }, [startup?.id, startup?.validationStatus, startup?.validationNotes]);

    const handleSaveStartupValidation = async () => {
        if (!startup?.startupId || !canEditStartupValidation) return;
        setSavingValidation(true);
        try {
            await adminAPI.updateStartupValidation(startup.startupId, {
                validationStatus: validationForm.status,
                validationNotes: validationForm.notes || ''
            });
            setStartup((prev) => ({
                ...prev,
                validationStatus: validationForm.status,
                validationNotes: validationForm.notes || '',
                validatedAt: validationForm.status === 'VALIDATED' ? new Date().toISOString() : null
            }));
            setSuccessMessage('Validation status saved');
            setShowSuccessDialog(true);
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Failed to save validation status';
            setErrorMessage(msg);
            setShowErrorDialog(true);
        } finally {
            setSavingValidation(false);
        }
    };

    const loadFiles = async () => {
        try {
            const response = await fetch(getApiUrl(`files?moduleType=STARTUP&moduleId=${id}`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                setFiles(data);
            } else if (response.status === 204) {
                // No content - no files found
                setFiles([]);
            }
        } catch (error) {
            console.error('Failed to load files:', error);
            setFiles([]);
        }
    };

    const loadDirectors = async () => {
        try {
            let data;
            if (user?.role === 'INVESTOR' || user?.role === 'INVESTOR_NETWORK' || user?.role === 'MANAGEMENT') {
                data = await investorAPI.getStartupDirectors(id);
            } else {
                data = await adminAPI.getDirectorsByUserId(id);
            }
            setDirectors(data || []);
        } catch (error) {
            console.error('Failed to load directors:', error);
            setDirectors([]);
        }
    };

    const loadInvestors = async () => {
        if (user?.role === 'INVESTOR') return;
        try {
            // INVESTOR_NETWORK / MANAGEMENT: admin investors API is forbidden for MANAGEMENT in SecurityConfig; use scoped list.
            if (user?.role === 'INVESTOR_NETWORK' || user?.role === 'MANAGEMENT') {
                const response = await fetch(getApiUrl('users/my-network/investors'), {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setInvestors(Array.isArray(data) ? data : []);
                } else {
                    setInvestors([]);
                }
                return;
            }
            const data = await adminAPI.getInvestors();
            setInvestors(data || []);
        } catch (error) {
            console.error('Failed to load investors:', error);
            setInvestors([]);
        }
    };

    const loadInvestments = async () => {
        try {
            let data;
            if (user?.role === 'INVESTOR') {
                data = await investorAPI.getStartupInvestments(id);
            } else {
                const response = await fetch(getApiUrl(`investments/startup/${id}`), {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (!response.ok) {
                    setInvestments([]);
                    return;
                }
                data = await response.json();
            }
            setInvestments(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to load investments:', error);
            setInvestments([]);
        }
    };

    const updateInvestmentAmount = async (investmentId, amount) => {
        const num = parseFloat(amount);
        if (isNaN(num) || num < 0) return;
        setUpdatingFunding(true);
        try {
            const response = await fetch(getApiUrl(`investments/${investmentId}/amount`), {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ amount: num })
            });
            if (response.ok) {
                setEditingFundingId(null);
                setEditingFundingAmount('');
                loadInvestments();
                setSuccessMessage('Funding amount updated.');
                setShowSuccessDialog(true);
            } else {
                const err = await response.json().catch(() => ({}));
                setErrorMessage(err.message || err.error || 'Failed to update amount');
                setShowErrorDialog(true);
            }
        } catch (error) {
            console.error('Update funding failed:', error);
            setErrorMessage('Failed to update funding amount.');
            setShowErrorDialog(true);
        } finally {
            setUpdatingFunding(false);
        }
    };

    const openTotalInvestmentEditor = () => {
        const items = Array.isArray(investments) ? investments : [];
        const split = {};
        let total = 0;
        items.forEach((inv) => {
            if (inv?.id == null) return;
            const amt = Number(inv.investmentAmount || 0);
            split[inv.id] = {
                amount: String(amt || ''),
                entrySharePrice:
                    inv.entrySharePrice != null && inv.entrySharePrice !== '' ? String(inv.entrySharePrice) : '',
                equityPercentage:
                    inv.equityPercentage != null && inv.equityPercentage !== '' ? String(inv.equityPercentage) : '',
                numberOfShares:
                    inv.numberOfShares != null && inv.numberOfShares !== '' ? String(inv.numberOfShares) : ''
            };
            total += amt;
        });
        setSplitLedgerDraft(split);
        splitLedgerInitialRef.current = Object.fromEntries(
            Object.entries(split).map(([k, v]) => [k, { ...v }])
        );
        setTotalInvestmentAmountInput(String(total || ''));
        setShowTotalInvestmentModal(true);
    };

    const mergeSplitLedgerRow = (investmentId, patch) => {
        setSplitLedgerDraft((prev) => ({
            ...prev,
            [investmentId]: {
                amount: '',
                entrySharePrice: '',
                equityPercentage: '',
                numberOfShares: '',
                ...prev[investmentId],
                ...patch
            }
        }));
    };

    const saveTotalInvestmentSplit = async () => {
        const items = (Array.isArray(investments) ? investments : []).filter((inv) => inv?.id != null);
        if (items.length === 0) {
            setErrorMessage('No investments found to update.');
            setShowErrorDialog(true);
            return;
        }
        const total = parseFloat(totalInvestmentAmountInput);
        if (isNaN(total) || total <= 0) {
            setErrorMessage('Enter a valid total investment amount.');
            setShowErrorDialog(true);
            return;
        }
        const payload = items.map((inv) => {
            const d = splitLedgerDraft[inv.id] || {};
            return {
                id: inv.id,
                amount: parseFloat(d.amount) || 0
            };
        });
        const splitTotal = payload.reduce((sum, row) => sum + row.amount, 0);
        if (Math.abs(splitTotal - total) > 0.01) {
            setErrorMessage('Split total must match the total investment amount.');
            setShowErrorDialog(true);
            return;
        }
        setSavingTotalInvestmentSplit(true);
        try {
            const toOptionalFloat = (v) => {
                if (v === '' || v == null) return null;
                const n = parseFloat(v);
                return Number.isFinite(n) ? n : null;
            };
            const toOptionalInt = (v) => {
                if (v === '' || v == null) return null;
                const n = parseInt(String(v), 10);
                return Number.isFinite(n) ? n : null;
            };
            await Promise.all(
                payload.map((row) => {
                    const d = splitLedgerDraft[row.id] || {};
                    const init = splitLedgerInitialRef.current[row.id] || {};
                    const body = { amount: row.amount };
                    if ((d.entrySharePrice || '') !== (init.entrySharePrice || '')) {
                        body.entrySharePrice = toOptionalFloat(d.entrySharePrice);
                    }
                    if ((d.equityPercentage || '') !== (init.equityPercentage || '')) {
                        body.equityPercentage = toOptionalFloat(d.equityPercentage);
                    }
                    if ((d.numberOfShares || '') !== (init.numberOfShares || '')) {
                        body.numberOfShares = toOptionalInt(d.numberOfShares);
                    }
                    return fetch(getApiUrl(`investments/${row.id}/amount`), {
                        method: 'PATCH',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        },
                        body: JSON.stringify(body)
                    }).then(async (res) => {
                        if (!res.ok) {
                            const err = await res.json().catch(() => ({}));
                            throw new Error(err.message || err.error || 'Failed to update one or more investments');
                        }
                        return res.json();
                    });
                })
            );
            setShowTotalInvestmentModal(false);
            setSuccessMessage('Total and split investment amounts updated successfully.');
            setShowSuccessDialog(true);
            loadInvestments();
        } catch (error) {
            setErrorMessage(error.message || 'Failed to update total investment split.');
            setShowErrorDialog(true);
        } finally {
            setSavingTotalInvestmentSplit(false);
        }
    };

    // Investment Tranches functions
    const loadInvestmentRounds = async () => {
        try {
            const roundsData = await investmentRoundAPI.getRoundsByStartup(id);
            setRounds(Array.isArray(roundsData) ? roundsData : []);
        } catch (error) {
            console.error('Failed to load investment rounds:', error);
            setRounds([]);
        }
    };

    const loadNetworksAndInvestors = async () => {
        if (user?.role === 'INVESTOR') {
            setNetworks([]);
            setAllInvestors([]);
            return;
        }
        // INVESTOR_NETWORK / MANAGEMENT cannot access admin users API
        if (user?.role === 'INVESTOR_NETWORK' || user?.role === 'MANAGEMENT') {
            const network = user?.role === 'INVESTOR_NETWORK'
                ? user
                : (user?.investorNetworkId ? { id: user.investorNetworkId, name: user.investorNetworkName || 'My Network' } : null);
            setNetworks(network ? [network] : []);
            setAllInvestors(Array.isArray(networkInvestors) ? networkInvestors : []);
            return;
        }
        try {
            const usersData = await adminAPI.getAllUsers();
            setNetworks((usersData || []).filter(u => u.role === 'INVESTOR_NETWORK'));
            setAllInvestors((usersData || []).filter(u => u.role === 'INVESTOR'));
        } catch (error) {
            console.error('Failed to load networks and investors:', error);
            setNetworks([]);
            setAllInvestors([]);
        }
    };

    const handleCreateRound = async () => {
        try {
            await investmentRoundAPI.createRound({
                ...roundForm,
                startupId: id,
                targetAmount: parseFloat(roundForm.targetAmount) || null,
                sharePrice: parseFloat(roundForm.sharePrice) || null,
                preMoneyValuation: parseFloat(roundForm.preMoneyValuation) || null,
                postMoneyValuation: parseFloat(roundForm.postMoneyValuation) || null,
                totalShares: parseInt(roundForm.totalShares) || null
            });
            setSuccessMessage('Investment tranche created successfully');
            setShowSuccessDialog(true);
            setShowRoundModal(false);
            resetRoundForm();
            loadInvestmentRounds();

            // If INVESTOR_NETWORK and amounts entered, record investments for this round (network + investors)
            if (user?.role === 'INVESTOR_NETWORK' && startup?.id) {
                const netAmt = parseFloat(roundInvestments.networkAmount);
                const investments = [];
                if (!isNaN(netAmt) && netAmt > 0) {
                    investments.push({ investorId: user.id, amount: netAmt });
                }
                (networkInvestors || []).forEach((inv) => {
                    const amt = parseFloat(roundInvestments.investorAmounts[inv.id]);
                    if (!isNaN(amt) && amt > 0) investments.push({ investorId: inv.id, amount: amt });
                });
                if (investments.length > 0) {
                    try {
                        const res = await fetch(getApiUrl('users/my-network/link-startup-with-investments'), {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            body: JSON.stringify({
                                startupId: Number(startup?.id ?? id),
                                investmentStage: roundForm.roundName,
                                currency: 'INR',
                                investmentType: 'EQUITY',
                                investments
                            })
                        });
                        if (res.ok) {
                            loadInvestments();
                            setSuccessMessage('Tranche created and ' + investments.length + ' investment(s) recorded.');
                        }
                    } catch (e) {
                        console.error('Record round investments failed:', e);
                    }
                }
            }
        } catch (error) {
            setErrorMessage(error.message || 'Failed to create tranche');
            setShowErrorDialog(true);
        }
    };

    const handleUpdateRound = async () => {
        try {
            await investmentRoundAPI.updateRound(editingRound.id, {
                ...roundForm,
                targetAmount: parseFloat(roundForm.targetAmount) || null,
                sharePrice: parseFloat(roundForm.sharePrice) || null,
                preMoneyValuation: parseFloat(roundForm.preMoneyValuation) || null,
                postMoneyValuation: parseFloat(roundForm.postMoneyValuation) || null,
                totalShares: parseInt(roundForm.totalShares) || null
            });
            setSuccessMessage('Tranche updated successfully');
            setShowSuccessDialog(true);
            setShowRoundModal(false);
            setEditingRound(null);
            resetRoundForm();
            loadInvestmentRounds();
        } catch (error) {
            setErrorMessage(error.message || 'Failed to update tranche');
            setShowErrorDialog(true);
        }
    };

    const handleCloseRound = async (round) => {
        if (!window.confirm(`Are you sure you want to close the "${round.roundName}" tranche? This action cannot be undone.`)) return;
        try {
            await investmentRoundAPI.closeRound(round.id);
            setSuccessMessage('Tranche closed successfully');
            setShowSuccessDialog(true);
            loadInvestmentRounds();
        } catch (error) {
            setErrorMessage(error.message || 'Failed to close tranche');
            setShowErrorDialog(true);
        }
    };

    const handleDeleteRound = async (round) => {
        if (!window.confirm(`Are you sure you want to delete the "${round.roundName}" tranche?`)) return;
        try {
            await investmentRoundAPI.deleteRound(round.id);
            setSuccessMessage('Tranche deleted successfully');
            setShowSuccessDialog(true);
            setSelectedRound(null);
            loadInvestmentRounds();
        } catch (error) {
            setErrorMessage(error.message || 'Failed to delete tranche');
            setShowErrorDialog(true);
        }
    };

    const handleCreateCommitment = async () => {
        try {
            await investmentRoundAPI.createCommitment(selectedRound.id, {
                networkId: commitmentForm.networkId,
                committedAmount: parseFloat(commitmentForm.committedAmount),
                equityPercentage: parseFloat(commitmentForm.equityPercentage) || null,
                numberOfShares: parseInt(commitmentForm.numberOfShares) || null,
                investmentType: commitmentForm.investmentType,
                notes: commitmentForm.notes,
                networkInvestmentAmount: parseFloat(commitmentForm.networkInvestmentAmount) || null,
                selectedMemberIds: commitmentForm.selectedMemberIds,
                commitmentDate: commitmentForm.commitmentDate || null,
                commitmentCurrency: commitmentForm.commitmentCurrency?.trim() || null,
                conditions: commitmentForm.conditions?.trim() || null
            });
            setSuccessMessage('Network commitment added to this tranche');
            setShowSuccessDialog(true);
            setShowCommitmentModal(false);
            resetCommitmentForm();
            const updatedRound = await investmentRoundAPI.getRound(selectedRound.id);
            setSelectedRound(updatedRound);
            loadInvestmentRounds();
        } catch (error) {
            setErrorMessage(error.message || 'Failed to create tranche');
            setShowErrorDialog(true);
        }
    };

    const handleUpdateCommitmentAmount = async (commitment) => {
        const value = parseFloat(editingCommittedAmount);
        if (isNaN(value) || value <= 0) {
            setErrorMessage('Committed amount must be greater than 0');
            setShowErrorDialog(true);
            return;
        }
        setUpdatingCommitmentAmount(true);
        try {
            const updatedCommitment = await investmentRoundAPI.updateCommitment(commitment.id, {
                committedAmount: value,
                commitmentDate: editingCommitmentMeta.commitmentDate || null,
                commitmentCurrency: editingCommitmentMeta.commitmentCurrency?.trim() || null,
                conditions: editingCommitmentMeta.conditions?.trim() || null
            });
            const nextCommitments = (selectedRound?.commitments || []).map((c) =>
                c.id === commitment.id ? { ...c, ...updatedCommitment } : c
            );
            setSelectedRound((prev) => (prev ? { ...prev, commitments: nextCommitments } : prev));
            if (selectedCommitment?.id === commitment.id) {
                setSelectedCommitment({ ...selectedCommitment, ...updatedCommitment });
            }
            setEditingCommitmentId(null);
            setEditingCommittedAmount('');
            setEditingCommitmentMeta({ commitmentDate: '', commitmentCurrency: 'INR', conditions: '' });
            setSuccessMessage('Commitment updated successfully');
            setShowSuccessDialog(true);
            loadInvestmentRounds();
        } catch (error) {
            setErrorMessage(error.message || 'Failed to update committed amount');
            setShowErrorDialog(true);
        } finally {
            setUpdatingCommitmentAmount(false);
        }
    };

    const handleCreateParticipation = async () => {
        try {
            await investmentRoundAPI.createParticipation(selectedCommitment.id, {
                ...participationForm,
                amount: parseFloat(participationForm.amount),
                participationPercentage: parseFloat(participationForm.participationPercentage) || null,
                sharesAllocated: parseInt(participationForm.sharesAllocated) || null
            });
            setSuccessMessage('Participation added successfully');
            setShowSuccessDialog(true);
            setShowParticipationModal(false);
            resetParticipationForm();
            const updatedCommitment = await investmentRoundAPI.getCommitment(selectedCommitment.id);
            setSelectedCommitment(updatedCommitment);
        } catch (error) {
            setErrorMessage(error.message || 'Failed to add participation');
            setShowErrorDialog(true);
        }
    };

    const handleCreateTerm = async () => {
        try {
            const targetCommitmentId = termCommitmentIdOverride || selectedCommitment?.id;
            if (!targetCommitmentId) {
                setErrorMessage('Please select a tranche first');
                setShowErrorDialog(true);
                return;
            }
            await investmentRoundAPI.createTerm(targetCommitmentId, {
                ...termForm,
                amount: parseFloat(termForm.amount),
                percentage: parseFloat(termForm.percentage) || null,
                releaseDate: termForm.releaseDate || null
            });
            setSuccessMessage('Term created successfully');
            setShowSuccessDialog(true);
            setShowTermModal(false);
            resetTermForm();
            const updatedCommitment = await investmentRoundAPI.getCommitment(targetCommitmentId);
            setSelectedCommitment(updatedCommitment);
            setTermCommitmentIdOverride(null);
        } catch (error) {
            setErrorMessage(error.message || 'Failed to create term');
            setShowErrorDialog(true);
        }
    };

    const handleMarkTermPaid = async (term) => {
        const reference = prompt('Enter transaction reference (optional):');
        if (reference === null) return; // User cancelled
        try {
            await investmentRoundAPI.markTermAsPaid(term.id, reference);
            setSuccessMessage('Term marked as paid');
            setShowSuccessDialog(true);
            const updatedCommitment = await investmentRoundAPI.getCommitment(selectedCommitment.id);
            setSelectedCommitment(updatedCommitment);
        } catch (error) {
            setErrorMessage(error.message || 'Failed to mark term as paid');
            setShowErrorDialog(true);
        }
    };

    const resetRoundForm = () => {
        setRoundForm({
            roundName: 'Seed',
            targetAmount: '',
            sharePrice: '',
            preMoneyValuation: '',
            postMoneyValuation: '',
            totalShares: '',
            openDate: new Date().toISOString().split('T')[0],
            closeDate: '',
            notes: ''
        });
        setRoundInvestments({ networkAmount: '', investorAmounts: {} });
    };

    const resetCommitmentForm = () => {
        setCommitmentForm({
            networkId: '',
            committedAmount: '',
            equityPercentage: '',
            numberOfShares: '',
            investmentType: 'EQUITY',
            notes: '',
            networkInvestmentAmount: '',
            selectedMemberIds: [],
            commitmentDate: '',
            commitmentCurrency: 'INR',
            conditions: ''
        });
        setNetworkMembers([]);
    };

    const loadNetworkMembers = async (networkId) => {
        if (!networkId) {
            setNetworkMembers([]);
            return;
        }
        try {
            const response = await fetch(getApiUrl(`investor-network/${networkId}/investors`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const members = await response.json();
                setNetworkMembers(members || []);
            }
        } catch (error) {
            console.error('Failed to load network members:', error);
            setNetworkMembers([]);
        }
    };

    const resetParticipationForm = () => {
        setParticipationForm({
            investorId: '',
            amount: '',
            participationPercentage: '',
            sharesAllocated: '',
            notes: ''
        });
    };

    const resetTermForm = () => {
        setTermForm({
            amount: '',
            percentage: '',
            dueDate: '',
            releaseDate: '',
            milestone: '',
            notes: ''
        });
    };

    const openEditRound = (round) => {
        setEditingRound(round);
        setRoundForm({
            roundName: round.roundName,
            targetAmount: round.targetAmount || '',
            sharePrice: round.sharePrice || '',
            preMoneyValuation: round.preMoneyValuation || '',
            postMoneyValuation: round.postMoneyValuation || '',
            totalShares: round.totalShares || '',
            openDate: round.openDate || '',
            closeDate: round.closeDate || '',
            notes: round.notes || ''
        });
        setShowRoundModal(true);
    };

    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const isNetworkInvestmentRow = (inv) => {
        if (!inv) return false;
        if (Number(inv?.investorId) === Number(effectiveInvestorNetworkId)) return true;
        if (inv?.investorEmail && user?.email && String(inv.investorEmail).toLowerCase() === String(user.email).toLowerCase()) return true;
        return false;
    };

    const sortedInvestmentsForNetworkView = [...(Array.isArray(investments) ? investments : [])].sort((a, b) => {
        const aIsNetwork = isNetworkInvestmentRow(a);
        const bIsNetwork = isNetworkInvestmentRow(b);
        if (aIsNetwork && !bIsNetwork) return -1;
        if (!aIsNetwork && bIsNetwork) return 1;
        return String(a?.investorName || '').localeCompare(String(b?.investorName || ''));
    });

    const splitEditorInvestments = [...sortedInvestmentsForNetworkView];

    const exportInvestmentsLedgerCsv = () => {
        const rows = sortedInvestmentsForNetworkView;
        const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const headers = [
            'Business Studio Investor', 'Email',
            'No CCPS', 'Holding %',
            'Gross Investment committed to Enfance', 'Partly Paid Amount',
            'Nett Subscription Amount', 'Issue Price',
            'Tranche-1 Subscription Amount', 'Tranche-1 No of CCPS', 'Tranche-1 Holding %',
            'Tranche-2 Subscription Amount', 'Tranche-2 No of CCPS', 'Tranche-2 Holding %',
            'Tranche-3 Subscription Amount', 'Tranche-3 No of CCPS', 'Tranche-3 Holding %',
            'Currency', 'Status'
        ];
        const lines = [headers.join(',')];
        rows.forEach((inv) => {
            lines.push(
                [
                    escape(inv.investorName),
                    escape(inv.investorEmail),
                    inv.numberOfShares ?? '',
                    inv.equityPercentage ?? '',
                    inv.investmentAmount ?? '',
                    inv.partlyPaidAmount ?? '',
                    inv.nettSubscriptionAmount ?? '',
                    inv.entrySharePrice ?? '',
                    inv.tranche1SubscriptionAmount ?? '',
                    inv.tranche1NoCcps ?? '',
                    inv.tranche1HoldingPercentage ?? '',
                    inv.tranche2SubscriptionAmount ?? '',
                    inv.tranche2NoCcps ?? '',
                    inv.tranche2HoldingPercentage ?? '',
                    inv.postCompletionSubscriptionAmount ?? '',
                    inv.postCompletionNoCcps ?? '',
                    inv.postCompletionHoldingPercentage ?? '',
                    inv.currency || 'INR',
                    escape(inv.status)
                ].join(',')
            );
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `investments-${startup?.organizationName || startup?.name || id || 'startup'}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const ledgerSearchQ = investmentLedgerSearch.trim().toLowerCase();
    const filteredLedgerInvestments = ledgerSearchQ
        ? sortedInvestmentsForNetworkView.filter((inv) => {
              const n = (inv.investorName || '').toLowerCase();
              const e = (inv.investorEmail || '').toLowerCase();
              return n.includes(ledgerSearchQ) || e.includes(ledgerSearchQ);
          })
        : sortedInvestmentsForNetworkView;

    const ledgerTrancheAllocationPct =
        startupTotalInvestmentAmount > 0
            ? Math.min(100, (Number(selectedTrancheTotalCommitted || 0) / Number(startupTotalInvestmentAmount)) * 100)
            : 0;
    const ledgerTrancheUtilPct = selectedRound?.progressPercentage != null
        ? Math.min(100, Number(selectedRound.progressPercentage))
        : 0;

    const investmentStatusLedgerClass = (s) => {
        if (!s) return 'bg-slate-100 text-slate-700';
        const u = String(s).toUpperCase();
        if (u === 'ACTIVE') return 'bg-emerald-100 text-emerald-900';
        if (u === 'PENDING') return 'bg-amber-100 text-amber-900';
        if (u === 'EXITED' || u === 'CANCELLED') return 'bg-rose-100 text-rose-800';
        return 'bg-slate-100 text-slate-700';
    };

    const getRoundStatusBadge = (status) => {
        const colors = {
            OPEN: 'bg-green-100 text-green-800',
            CLOSED: 'bg-gray-100 text-gray-800',
            CANCELLED: 'bg-red-100 text-red-800',
            COMMITTED: 'bg-blue-100 text-blue-800',
            PARTIAL: 'bg-yellow-100 text-yellow-800',
            COMPLETED: 'bg-green-100 text-green-800',
            PENDING: 'bg-yellow-100 text-yellow-800',
            PAID: 'bg-green-100 text-green-800',
            OVERDUE: 'bg-red-100 text-red-800',
            ACTIVE: 'bg-green-100 text-green-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getMilestoneRows = () => {
        const rows = [];
        (Array.isArray(rounds) ? rounds : []).forEach((round) => {
            (Array.isArray(round.commitments) ? round.commitments : []).forEach((commitment) => {
                (Array.isArray(commitment.terms) ? commitment.terms : []).forEach((term) => {
                    rows.push({
                        id: term.id,
                        roundName: round.roundName || '-',
                        networkName: commitment.networkName || '-',
                        termNumber: term.termNumber,
                        milestone: term.milestone || 'N/A',
                        dueDate: term.dueDate || null,
                        amount: term.amount,
                        status: term.status || 'PENDING'
                    });
                });
            });
        });

        rows.sort((a, b) => {
            if (!a.dueDate && !b.dueDate) return 0;
            if (!a.dueDate) return 1;
            if (!b.dueDate) return -1;
            return a.dueDate.localeCompare(b.dueDate);
        });

        return rows;
    };

    const getMilestoneTargets = () => {
        const targets = [];
        (Array.isArray(rounds) ? rounds : []).forEach((round) => {
            (Array.isArray(round.commitments) ? round.commitments : []).forEach((commitment) => {
                targets.push({
                    id: commitment.id,
                    label: `${round.roundName || 'Tranche'} - ${commitment.networkName || 'Network'}`
                });
            });
        });
        return targets;
    };

    const loadStageHistory = async () => {
        try {
            // TODO: Implement stage history loading
            setStageHistory([
                { stage: 'Idea', date: '2023-01-15', amount: null },
                { stage: 'Seed', date: '2023-06-20', amount: '$500K' },
                { stage: 'Series A', date: '2024-03-10', amount: '$2M' }
            ]);
        } catch (error) {
            console.error('Failed to load stage history:', error);
        }
    };

    const openEditModal = () => {
        if (!canEditStartupDetails) return;
        setEditData({
            companyName: startup.organizationName || '',
            industry: startup.department || '',
            stage: startup.jobTitle || '',
            description: startup.description || '',
            website: startup.website || '',
            phoneNumber: startup.phoneNumber || ''
        });
        setShowEditModal(true);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!canEditStartupDetails) {
            setAlert({ isOpen: true, title: 'Permission denied', message: 'Only admin or startup users can edit startup details.', type: 'error' });
            return;
        }
        try {
            const response = await fetch(getApiUrl(`admin/users/${id}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(editData)
            });

            if (response.ok) {
                setShowEditModal(false);
                loadStartupDetails();
                setAlert({ isOpen: true, title: 'Success', message: 'Startup updated successfully!', type: 'success' });
            } else {
                setAlert({ isOpen: true, title: 'Error', message: 'Failed to update startup', type: 'error' });
            }
        } catch (error) {
            console.error('Failed to update startup:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to update startup', type: 'error' });
        }
    };

    const handleStageSelect = async (newStage) => {
        if (!canEditStartupDetails) {
            setAlert({ isOpen: true, title: 'Permission denied', message: 'Only admin or startup users can edit startup details.', type: 'error' });
            return;
        }
        const previousStage = startup.jobTitle;
        // Optimistic update
        setStartup(prev => ({ ...prev, jobTitle: newStage }));

        try {
            const payload = {
                companyName: startup.organizationName,
                industry: startup.department,
                stage: newStage,
                description: startup.description,
                website: startup.website,
                phoneNumber: startup.phoneNumber
            };

            const response = await fetch(getApiUrl(`admin/users/${id}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMsg = errorData.message || errorData.error || 'Failed to update stage';
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error('Failed to update stage:', error);
            // Revert on failure
            setStartup(prev => ({ ...prev, jobTitle: previousStage }));
            setErrorMessage(error.message || 'Failed to update stage. Please try again.');
            setShowErrorDialog(true);
        }
    };

    const handleDeleteStartup = async () => {
        try {
            await fetch(getApiUrl(`admin/users/${id}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            navigate(`/${tenantId}/startups`);
        } catch (error) {
            console.error('Failed to delete startup:', error);
        }
    };

    // handleFileUpload function removed as it is replaced by FileUpload component

    const handleAddDirector = async () => {
        if (!canManageStartupDirectors) {
            setErrorMessage('You do not have permission to add directors.');
            setShowErrorDialog(true);
            return;
        }
        if (!newDirector.panNumber || !newDirector.aadharNumber) {
            setErrorMessage('PAN Number and Aadhaar Number are mandatory.');
            setShowErrorDialog(true);
            return;
        }

        try {
            const directorData = {
                name: newDirector.name,
                email: newDirector.email || null,
                phone: newDirector.phone || null,
                role: newDirector.role || null,
                category: newDirector.category || null,
                shareholding: newDirector.shareholding || null,
                aadharNumber: newDirector.aadharNumber,
                panNumber: newDirector.panNumber
            };
            const createdDirector = await adminAPI.createDirectorByUserId(id, directorData);
            setDirectors([...directors, createdDirector]);
            setNewDirector({ name: '', email: '', phone: '', role: '', category: '', shareholding: '', aadharNumber: '', panNumber: '' });
            setShowAddDirectorModal(false);
            setSuccessMessage('Director added successfully!');
            setShowSuccessDialog(true);
        } catch (error) {
            console.error('Failed to add director:', error);
            setErrorMessage(error.response?.data?.message || 'Failed to add director');
            setShowErrorDialog(true);
        }
    };

    const handleEditDirector = (director) => {
        setEditingDirector(director);
        setNewDirector({
            name: director.name || '',
            email: director.email || '',
            phone: director.phone || '',
            role: director.role || '',
            category: director.category || '',
            shareholding: director.shareholding || '',
            aadharNumber: director.aadharNumber || '',
            panNumber: director.panNumber || ''
        });
        setShowEditDirectorModal(true);
    };

    const handleUpdateDirector = async () => {
        if (!canManageStartupDirectors) {
            setErrorMessage('You do not have permission to update directors.');
            setShowErrorDialog(true);
            return;
        }
        if (!newDirector.panNumber || !newDirector.aadharNumber) {
            setErrorMessage('PAN Number and Aadhaar Number are mandatory.');
            setShowErrorDialog(true);
            return;
        }

        try {
            const directorData = {
                name: newDirector.name,
                email: newDirector.email || null,
                phone: newDirector.phone || null,
                role: newDirector.role || null,
                category: newDirector.category || null,
                shareholding: newDirector.shareholding || null,
                aadharNumber: newDirector.aadharNumber,
                panNumber: newDirector.panNumber
            };
            const updatedDirector = await adminAPI.updateDirector(editingDirector.id, directorData);
            setDirectors(directors.map(d => d.id === editingDirector.id ? updatedDirector : d));
            setNewDirector({ name: '', email: '', phone: '', role: '', category: '', shareholding: '', aadharNumber: '', panNumber: '' });
            setEditingDirector(null);
            setShowEditDirectorModal(false);
            setSuccessMessage('Director updated successfully!');
            setShowSuccessDialog(true);
        } catch (error) {
            console.error('Failed to update director:', error);
            setErrorMessage(error.response?.data?.message || 'Failed to update director');
            setShowErrorDialog(true);
        }
    };

    const handleDeleteDirector = async (directorId) => {
        if (!canManageStartupDirectors) {
            setErrorMessage('You do not have permission to delete directors.');
            setShowErrorDialog(true);
            return;
        }
        try {
            await adminAPI.deleteDirector(directorId);
            setDirectors(directors.filter(d => d.id !== directorId));
            setSuccessMessage('Director deleted successfully!');
            setShowSuccessDialog(true);
        } catch (error) {
            console.error('Failed to delete director:', error);
            setErrorMessage(error.response?.data?.message || 'Failed to delete director');
            setShowErrorDialog(true);
        }
    };

    const handleDeleteFile = (fileId, fileName) => {
        setConfirm({
            isOpen: true,
            title: 'Delete File',
            message: `Are you sure you want to delete "${fileName}"?`,
            type: 'danger',
            onConfirm: async () => {
                try {
                    await fileAPI.deleteFile(fileId);
                    loadFiles();
                    setSuccessMessage('File deleted successfully');
                    setShowSuccessDialog(true);
                } catch (err) {
                    console.error('Failed to delete file:', err);
                    setErrorMessage('Failed to delete file');
                    setShowErrorDialog(true);
                }
            }
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!startup) {
        return (
            <div className="p-6">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900">Startup not found</h2>
                    <button onClick={() => navigate(user?.role === 'INVESTOR' ? `/${tenantId}/investor/dashboard` : user?.role === 'INVESTOR_NETWORK' || user?.role === 'MANAGEMENT' ? `/${tenantId}/investor-network/startups` : `/${tenantId}/startups`)} className="mt-4 text-blue-600 hover:text-blue-800">
                        ← Back to {user?.role === 'INVESTOR' ? 'Dashboard' : user?.role === 'INVESTOR_NETWORK' || user?.role === 'MANAGEMENT' ? 'Portfolio' : 'Startups'}
                    </button>
                </div>
            </div>
        );
    }

    const tenantPath = tenantId || user?.tenantIdentifier || 'default';

    const mainContent = (
        <>
            {/* Page Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-4">
                            <button onClick={() => navigate(user?.role === 'INVESTOR' ? `/${tenantId}/investor/dashboard` : user?.role === 'INVESTOR_NETWORK' || user?.role === 'MANAGEMENT' ? `/${tenantId}/investor-network/startups` : `/${tenantId}/startups`)} className="text-gray-600 hover:text-gray-900">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div className="flex items-center space-x-4">
                                <h1 className="text-2xl font-bold text-gray-900 flex flex-wrap items-center gap-2">
                                    <span>{startup.organizationName || 'Startup Name'}</span>
                                    {isPortfolioView && (
                                        <span className="align-middle text-xs font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-100 border border-emerald-200/80 px-2 py-0.5 rounded-md">
                                            Portfolio
                                        </span>
                                    )}
                                    {(startup.startupId != null || startup.validationStatus) && (
                                        <span
                                            className={`align-middle text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md border ${startupValidationBadgeClass(startup.validationStatus || 'PENDING')}`}
                                            title="Platform validation status"
                                        >
                                            {startupValidationLabel(startup.validationStatus || 'PENDING')}
                                        </span>
                                    )}
                                </h1>
                                <div className="h-6 w-px bg-gray-300"></div>
                                <div className="flex items-center space-x-2">
                                    <span className="text-sm font-medium text-gray-500">Founder:</span>
                                    <span className="text-sm font-semibold text-gray-900">{startup.name}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            {/* Admin-only prev/next navigation */}
                            {(user?.role === 'ADMIN' || user?.role === 'TENANT_ADMIN') && (
                                <DetailViewNavigation type="startup" currentId={id} />
                            )}

                            {/* Action Buttons */}
                            {/* Action Buttons - Only for Admin */}
                            {canEditStartupDetails && (
                                <div className="flex space-x-3">
                                    <button onClick={openEditModal} className="p-2 text-gray-400 hover:text-blue-600 rounded-full hover:bg-blue-50 transition" title="Edit Startup">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    </button>
                                    <button onClick={() => setShowDeleteModal(true)} className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50 transition" title="Delete Startup">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Stage Timeline - Full Width Row */}
                    <div className="w-full">
                        <StartupStageTimeline
                            currentStage={startup.jobTitle}
                            onStageSelect={canEditStartupDetails ? handleStageSelect : undefined}
                        />
                    </div>

                    {/* Tabs */}
                    <div className="mt-4 border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                            {[
                                { id: 'overview', label: 'Overview' },
                                { id: 'files', label: 'Files' },
                                { id: 'investments', label: 'Investments' },
                                { id: 'milestones', label: 'Milestones' },
                                { id: 'stage-history', label: 'Stage History' },
                                { id: 'directors', label: 'Directors' }
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => {
                                        setActiveTab(tab.id);
                                        setSearchParams({ tab: tab.id });
                                    }}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                {/* Overview Tab */}
                {activeTab === 'overview' && startup && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                            {/* Quick Stats */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-sm font-medium text-gray-500 mb-4">Quick Stats</h3>
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-2xl font-bold text-gray-900">{files.length || 0}</p>
                                        <p className="text-sm text-gray-500">Documents</p>
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-gray-900">{directors.length || 0}</p>
                                        <p className="text-sm text-gray-500">Directors</p>
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-gray-900">{stageHistory.length || 0}</p>
                                        <p className="text-sm text-gray-500">Funding Tranches</p>
                                    </div>
                                </div>
                            </div>

                            {/* Company Info */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
                                <dl className="space-y-3">
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{startup.organizationName || startup.name || 'N/A'}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Industry</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{startup.department || startup.industry || 'N/A'}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Current Stage</dt>
                                        <dd className="mt-1">
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                                {startup.jobTitle || startup.stage || 'Not specified'}
                                            </span>
                                        </dd>
                                    </div>
                                    {(startup.website || startup.organizationType) && (
                                        <>
                                            {startup.website && (
                                                <div>
                                                    <dt className="text-sm font-medium text-gray-500">Website</dt>
                                                    <dd className="mt-1 text-sm text-gray-900">
                                                        <a href={startup.website.startsWith('http') ? startup.website : `https://${startup.website}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-600 hover:text-blue-800">
                                                            {startup.website}
                                                        </a>
                                                    </dd>
                                                </div>
                                            )}
                                            {startup.organizationType && (
                                                <div>
                                                    <dt className="text-sm font-medium text-gray-500">Category</dt>
                                                    <dd className="mt-1 text-sm text-gray-900">{startup.organizationType}</dd>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </dl>
                            </div>

                            {/* Contact Info */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                                <dl className="space-y-3">
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Primary Contact</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{startup.name || 'N/A'}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Email</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{startup.email || 'N/A'}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Phone</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{startup.phoneNumber || 'N/A'}</dd>
                                    </div>
                                </dl>
                            </div>

                            {/* Company Location */}
                            {(startup.officialAddress || startup.branchAddress || startup.city || startup.state || startup.country || startup.address) && (
                                <div className="bg-white rounded-lg shadow p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Location</h3>
                                    <dl className="space-y-3">
                                        {startup.officialAddress && (
                                            <div>
                                                <dt className="text-sm font-medium text-gray-500">Official Address</dt>
                                                <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">{startup.officialAddress}</dd>
                                            </div>
                                        )}
                                        {startup.branchAddress && (
                                            <div>
                                                <dt className="text-sm font-medium text-gray-500">Branch Address</dt>
                                                <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">{startup.branchAddress}</dd>
                                            </div>
                                        )}
                                        {startup.address && !startup.officialAddress && (
                                            <div>
                                                <dt className="text-sm font-medium text-gray-500">Address</dt>
                                                <dd className="mt-1 text-sm text-gray-900">{startup.address}</dd>
                                            </div>
                                        )}
                                        {(startup.city || startup.state || startup.country) && (
                                            <div>
                                                <dt className="text-sm font-medium text-gray-500">Location</dt>
                                                <dd className="mt-1 text-sm text-gray-900">
                                                    {[startup.city, startup.state, startup.country].filter(Boolean).join(', ') || 'N/A'}
                                                </dd>
                                            </div>
                                        )}
                                    </dl>
                                </div>
                            )}
                        </div>

                        {startup.startupId != null && (
                            <div className="bg-white rounded-lg shadow p-6 border border-gray-100">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Platform validation</h3>
                                <p className="text-sm text-gray-500 mb-4">
                                    Review status for this startup profile. Startups see this on their dashboard.
                                </p>
                                {canEditStartupValidation ? (
                                    <div className="space-y-4 max-w-xl">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                            <select
                                                value={validationForm.status}
                                                onChange={(e) => setValidationForm((f) => ({ ...f, status: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="PENDING">Pending review</option>
                                                <option value="VALIDATED">Validated</option>
                                                <option value="REJECTED">Rejected</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                                            <textarea
                                                value={validationForm.notes}
                                                onChange={(e) => setValidationForm((f) => ({ ...f, notes: e.target.value }))}
                                                rows={3}
                                                placeholder="Feedback visible to the startup team when relevant"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleSaveStartupValidation}
                                            disabled={savingValidation}
                                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                        >
                                            {savingValidation ? 'Saving…' : 'Save validation'}
                                        </button>
                                    </div>
                                ) : (
                                    <dl className="space-y-2">
                                        <div>
                                            <dt className="text-sm font-medium text-gray-500">Status</dt>
                                            <dd className="mt-1">
                                                <span
                                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md border ${startupValidationBadgeClass(startup.validationStatus || 'PENDING')}`}
                                                >
                                                    {startupValidationLabel(startup.validationStatus || 'PENDING')}
                                                </span>
                                            </dd>
                                        </div>
                                        {startup.validatedAt && (
                                            <div>
                                                <dt className="text-sm font-medium text-gray-500">Validated at</dt>
                                                <dd className="mt-1 text-sm text-gray-900">
                                                    {new Date(startup.validatedAt).toLocaleString()}
                                                </dd>
                                            </div>
                                        )}
                                        {startup.validationNotes && (
                                            <div>
                                                <dt className="text-sm font-medium text-gray-500">Notes</dt>
                                                <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{startup.validationNotes}</dd>
                                            </div>
                                        )}
                                    </dl>
                                )}
                            </div>
                        )}

                        {/* Investors in this startup */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Investors & funding</h3>
                            <p className="text-sm text-gray-500 mb-4">Investors who have invested in this startup. You can update funding amounts below.</p>
                            {(Array.isArray(investments) && investments.length > 0) ? (
                                <div className="space-y-3">
                                    {investments.map((inv) => (
                                        <div key={inv.id} className="flex items-center justify-between py-3 px-3 rounded-lg border border-gray-100 hover:bg-gray-50/50 transition">
                                            <div className="flex items-center min-w-0">
                                                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold mr-3 flex-shrink-0">
                                                    {(inv.investorName || 'I').charAt(0)}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-medium text-gray-900 truncate">{inv.investorName || '—'}</p>
                                                    <p className="text-xs text-gray-500 truncate">{inv.investorEmail}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 flex-shrink-0">
                                                {editingFundingId === inv.id ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="1"
                                                            value={editingFundingAmount}
                                                            onChange={(e) => setEditingFundingAmount(e.target.value)}
                                                            className="w-28 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => updateInvestmentAmount(inv.id, editingFundingAmount)}
                                                            disabled={updatingFunding}
                                                            className="px-2 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                                        >
                                                            {updatingFunding ? 'Saving...' : 'Save'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => { setEditingFundingId(null); setEditingFundingAmount(''); }}
                                                            className="px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="text-right text-sm">
                                                            {inv.investmentAmount != null && (
                                                                <span className="font-semibold text-gray-900">
                                                                    {new Intl.NumberFormat('en-IN', { style: 'currency', currency: inv.currency || 'INR' }).format(inv.investmentAmount)}
                                                                </span>
                                                            )}
                                                            {inv.equityPercentage != null && (
                                                                <span className="ml-2 text-gray-600">{inv.equityPercentage}%</span>
                                                            )}
                                                            <span className={`ml-2 inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${inv.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                                {inv.status || '—'}
                                                            </span>
                                                        </div>
                                                        {(user?.role === 'ADMIN' || user?.role === 'TENANT_ADMIN' || user?.role === 'INVESTOR_NETWORK') && (
                                                            <button
                                                                type="button"
                                                                onClick={() => { setEditingFundingId(inv.id); setEditingFundingAmount(String(inv.investmentAmount ?? '')); }}
                                                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                                                title="Update funding amount"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-6 text-gray-500">
                                    <p>No investors yet. Investments will appear here when recorded.</p>
                                </div>
                            )}
                        </div>

                        {/* Investor Networks linked to this startup (from tranches/commitments) */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Investor Networks</h3>
                            <p className="text-sm text-gray-500 mb-4">Networks with commitments in this startup&apos;s tranches</p>
                            {(() => {
                                const networkSet = new Map();
                                (rounds || []).forEach((r) => {
                                    (r.commitments || []).forEach((c) => {
                                        if (c.networkId && c.networkName) networkSet.set(c.networkId, c.networkName);
                                    });
                                });
                                const networkList = Array.from(networkSet.entries()).map(([nid, name]) => ({ id: nid, name }));
                                return networkList.length > 0 ? (
                                    <ul className="space-y-2">
                                        {networkList.map((n) => (
                                            <li key={n.id} className="flex items-center py-2">
                                                <span className="inline-flex h-8 w-8 rounded-full bg-purple-100 text-purple-700 font-medium items-center justify-center mr-3 text-sm">
                                                    {(n.name || 'N').charAt(0)}
                                                </span>
                                                <span className="font-medium text-gray-900">{n.name}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="text-center py-6 text-gray-500">
                                        <p>No investor networks yet. Add tranches and network commitments in the Investments tab.</p>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Recent Activity */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                            <div className="text-center py-8 text-gray-500">
                                <p>No recent activity</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Files Tab */}
                {activeTab === 'files' && (() => {
                    // Get all unique document types from files
                    const getAllDocumentTypes = () => {
                        const types = new Set();
                        files.forEach(file => {
                            if (file.documentType || file.fileCategory) {
                                types.add(file.documentType || file.fileCategory);
                            }
                        });
                        return Array.from(types).sort();
                    };

                    // Filter files based on selected document type
                    const filteredFiles = files.filter(file => {
                        const docType = file.documentType || file.fileCategory;
                        return selectedDocumentType === 'ALL' || docType === selectedDocumentType;
                    });

                    return (
                        <div className="space-y-4">
                            <div className="bg-white rounded-lg shadow">
                                {/* Header */}
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <div className="flex justify-between items-center flex-wrap gap-3">
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-900">All files</h2>
                                            <p className="text-sm text-gray-500 mt-0.5">All documents uploaded for this startup ({files.length} file{files.length !== 1 ? 's' : ''})</p>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <label className="text-sm font-medium text-gray-700">Filter by type:</label>
                                            <select
                                                value={selectedDocumentType}
                                                onChange={(e) => setSelectedDocumentType(e.target.value)}
                                                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="ALL">All Types</option>
                                                {getAllDocumentTypes().map(type => (
                                                    <option key={type} value={type}>
                                                        {getCategoryLabel(type)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Compact File Upload - Hide for Investor */}
                                {user?.role !== 'INVESTOR' && (
                                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                        <FileUpload
                                            moduleType="STARTUP"
                                            moduleId={startup?.id ?? id}
                                            onUploadSuccess={() => {
                                                loadFiles();
                                                setSuccessMessage('File uploaded successfully!');
                                                setShowSuccessDialog(true);
                                            }}
                                            onUploadError={(msg) => {
                                                setErrorMessage(msg || 'Failed to upload file');
                                                setShowErrorDialog(true);
                                            }}
                                        />
                                    </div>
                                )}

                                {/* File List */}
                                {filteredFiles.length === 0 ? (
                                    <div className="px-6 py-12 text-center">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <h3 className="mt-2 text-sm font-medium text-gray-900">
                                            {files.length === 0 ? 'No files' : 'No files match the selected filter'}
                                        </h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            {files.length === 0
                                                ? 'Upload pitch decks, financial statements, business plans, etc.'
                                                : 'Try selecting a different document type filter.'}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-gray-200">
                                        {filteredFiles.map((file) => {
                                            const docType = file.documentType || file.fileCategory;
                                            return (
                                                <div
                                                    key={file.id}
                                                    className="px-6 py-3 hover:bg-gray-50 transition cursor-pointer"
                                                    onClick={() => {
                                                        setPreviewFile(file);
                                                        setShowPreviewModal(true);
                                                    }}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        {/* File Info */}
                                                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                                                            <div className="flex-shrink-0">
                                                                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                </svg>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center space-x-2 mb-1">
                                                                    <p className="text-sm font-medium text-gray-900 truncate" title={file.originalName}>
                                                                        {file.originalName}
                                                                    </p>
                                                                    {docType && (
                                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(docType)}`}>
                                                                            {getCategoryLabel(docType)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center space-x-3 mt-1">
                                                                    <p className="text-xs text-gray-500">
                                                                        {(file.fileSize / 1024 / 1024).toFixed(2)} MB
                                                                    </p>
                                                                    {file.uploadedByName && (
                                                                        <>
                                                                            <span className="text-xs text-gray-400">•</span>
                                                                            <p className="text-xs text-gray-500">
                                                                                Uploaded by {file.uploadedByName}
                                                                            </p>
                                                                            {file.uploadedAt && (
                                                                                <>
                                                                                    <span className="text-xs text-gray-400">•</span>
                                                                                    <p className="text-xs text-gray-500">
                                                                                        {new Date(file.uploadedAt).toLocaleDateString()}
                                                                                    </p>
                                                                                </>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                    {file.permissions && file.permissions.length > 0 && (
                                                                        <>
                                                                            <span className="text-xs text-gray-400">•</span>
                                                                            <p className="text-xs text-blue-600">
                                                                                {file.permissionCount || file.permissions.length} permission{file.permissionCount !== 1 ? 's' : ''}
                                                                            </p>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Actions */}
                                                        <div className="flex items-center space-x-2 ml-4">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setPreviewFile(file);
                                                                    setShowPreviewModal(true);
                                                                }}
                                                                className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100 transition inline-flex items-center space-x-1"
                                                            >
                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                </svg>
                                                                <span>Preview</span>
                                                            </button>
                                                            {user?.role !== 'INVESTOR' && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedFileForPermissions(file);
                                                                        setShowPermissionsModal(true);
                                                                    }}
                                                                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition inline-flex items-center space-x-1"
                                                                >
                                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                                    </svg>
                                                                    <span>Permissions</span>
                                                                </button>
                                                            )}
                                                            {(user?.id === file.uploadedById ||
                                                                user?.role === 'ADMIN' ||
                                                                (user?.role === 'STARTUP' && user?.id === startup?.id) ||
                                                                (file.permissions && file.permissions.some(p => p.userId === user?.id && p.permissionType === 'DELETE'))) && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDeleteFile(file.id, file.originalName);
                                                                        }}
                                                                        className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 transition inline-flex items-center space-x-1"
                                                                        title="Delete File"
                                                                    >
                                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* Investments Tab — ledger-style layout */}
                {activeTab === 'investments' && (
                    <div className="space-y-6 font-ledger antialiased">
                        <div className="rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden">
                            <div className="bg-gradient-to-b from-slate-50 to-slate-100/90 px-6 md:px-8 pt-8 pb-4">
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                                    <div className="lg:col-span-2 relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 text-white p-8 min-h-[220px] flex flex-col justify-between shadow-lg ring-1 ring-white/10">
                                        <div className="relative z-10">
                                            <p className="text-blue-200/90 text-sm font-semibold mb-1 tracking-wide">Total investment amount (startup)</p>
                                            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">{formatCurrency(startupTotalInvestmentAmount)}</h2>
                                            <p className="text-slate-400 text-xs mt-2">Across all tranches (raised on rounds)</p>
                                        </div>
                                        <div className="relative z-10 flex flex-wrap justify-between items-end gap-4 mt-6">
                                            <div>
                                                <p className="text-slate-400 text-[10px] uppercase tracking-widest mb-1 font-semibold">Investor allocations</p>
                                                <p className="text-lg font-bold">{sortedInvestmentsForNetworkView.length} recorded</p>
                                            </div>
                                            <div className="flex -space-x-2">
                                                {sortedInvestmentsForNetworkView.slice(0, 4).map((inv) => (
                                                    <div
                                                        key={inv.id}
                                                        className="w-10 h-10 rounded-full border-2 border-slate-900 bg-slate-700 flex items-center justify-center text-xs font-bold text-white"
                                                        title={inv.investorName || ''}
                                                    >
                                                        {(inv.investorName || 'I').charAt(0).toUpperCase()}
                                                    </div>
                                                ))}
                                                {sortedInvestmentsForNetworkView.length > 4 && (
                                                    <div className="w-10 h-10 rounded-full border-2 border-slate-900 bg-slate-600 flex items-center justify-center text-[10px] font-bold text-white">
                                                        +{sortedInvestmentsForNetworkView.length - 4}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="absolute -right-6 -bottom-6 opacity-[0.07] pointer-events-none text-white">
                                            <svg className="w-44 h-44" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="rounded-xl border border-slate-200/80 bg-white p-6 flex flex-col justify-between shadow-sm">
                                        <div>
                                            <div className="flex justify-between items-start mb-3">
                                                <p className="text-slate-500 text-sm font-semibold">
                                                    Selected tranche{selectedRound?.roundName ? ` (${selectedRound.roundName})` : ''}
                                                </p>
                                                <span className="text-slate-400" title="Commitments in this round vs startup total">
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                </span>
                                            </div>
                                            <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                                                {selectedRound ? formatCurrency(selectedTrancheTotalCommitted) : '—'}
                                            </h3>
                                            <p className="text-xs text-slate-500 mt-2">
                                                Allocation:{' '}
                                                <span className="text-slate-900 font-bold">{ledgerTrancheAllocationPct.toFixed(1)}%</span> of startup total
                                            </p>
                                            {selectedRound && (
                                                <p className="text-xs text-slate-500 mt-1">
                                                    Share price {formatCurrency(selectedRound.sharePrice)} · Total shares{' '}
                                                    {selectedRound.totalShares != null ? Number(selectedRound.totalShares).toLocaleString() : '—'}
                                                </p>
                                            )}
                                        </div>
                                        <div className="mt-6 pt-6 border-t border-slate-100">
                                            <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                                                <div
                                                    className="bg-slate-900 h-full rounded-full transition-all duration-500"
                                                    style={{ width: `${ledgerTrancheUtilPct}%` }}
                                                />
                                            </div>
                                            <div className="flex justify-between mt-3">
                                                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Tranche utilization</span>
                                                <span className="text-[10px] text-slate-900 font-bold">{ledgerTrancheUtilPct.toFixed(1)}% raised vs target</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-6 md:gap-10 border-b border-slate-200/80" role="tablist" aria-label="Investments sections">
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={investmentSubTab === 'investments'}
                                        onClick={() => setInvestmentSubTab('investments')}
                                        className={`pb-4 text-sm font-bold border-b-2 transition-colors relative flex items-center gap-2 ${investmentSubTab === 'investments'
                                            ? 'text-slate-900 border-slate-900'
                                            : 'text-slate-500 border-transparent hover:text-slate-800'}`}
                                    >
                                        Investments
                                        <span className="bg-slate-900 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                                            {sortedInvestmentsForNetworkView.length}
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={investmentSubTab === 'tranches'}
                                        onClick={() => setInvestmentSubTab('tranches')}
                                        className={`pb-4 text-sm font-semibold border-b-2 transition-colors ${investmentSubTab === 'tranches'
                                            ? 'text-slate-900 border-slate-900'
                                            : 'text-slate-500 border-transparent hover:text-slate-800'}`}
                                    >
                                        Investment tranches
                                    </button>
                                </div>
                            </div>

                            <div className="px-6 md:px-8 py-6 bg-white">
                            {/* Investments Sub-tab Content */}
                            {investmentSubTab === 'investments' && (
                                <div>
                                    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                                        <div className="flex flex-wrap items-center gap-3">
                                            <div className="relative hidden sm:block">
                                                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                                <input
                                                    type="search"
                                                    value={investmentLedgerSearch}
                                                    onChange={(e) => setInvestmentLedgerSearch(e.target.value)}
                                                    placeholder="Search investors…"
                                                    className="pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm w-64 focus:ring-2 focus:ring-slate-900/15 focus:border-slate-300 outline-none transition-all"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSuccessMessage('Advanced filters are coming soon.');
                                                    setShowSuccessDialog(true);
                                                }}
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all"
                                            >
                                                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                                </svg>
                                                Filter
                                            </button>
                                            <button
                                                type="button"
                                                onClick={exportInvestmentsLedgerCsv}
                                                disabled={sortedInvestmentsForNetworkView.length === 0}
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all disabled:opacity-40"
                                            >
                                                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                                Export CSV
                                            </button>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            {canEditNetworkTotalInvestment && (Array.isArray(investments) ? investments : []).length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={openTotalInvestmentEditor}
                                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-slate-900 to-blue-900 shadow-sm hover:opacity-95 active:scale-[0.98] transition-all"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                    Edit total & split
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="rounded-xl border border-slate-200/80 overflow-hidden shadow-sm bg-white">
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left border-collapse min-w-[860px]">
                                                <thead>
                                                    <tr className="bg-slate-50/80 border-b border-slate-200/80">
                                                        <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">Investor</th>
                                                        <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">Amount</th>
                                                        <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">Post holding %</th>
                                                        <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">No. of CCPS</th>
                                                        <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">Issue price</th>
                                                        <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">Status</th>
                                                        {(user?.role === 'ADMIN' || user?.role === 'TENANT_ADMIN' || user?.role === 'INVESTOR_NETWORK' || user?.role === 'MANAGEMENT') && (
                                                            <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest text-right">Actions</th>
                                                        )}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100">
                                                    {(Array.isArray(investments) ? investments : []).length === 0 ? (
                                                        <tr>
                                                            <td colSpan={(user?.role === 'ADMIN' || user?.role === 'TENANT_ADMIN' || user?.role === 'INVESTOR_NETWORK' || user?.role === 'MANAGEMENT') ? 7 : 6} className="px-6 py-14 text-center text-slate-500">
                                                                <svg className="mx-auto h-12 w-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                                </svg>
                                                                <h3 className="mt-2 text-sm font-bold text-slate-900">No investments found</h3>
                                                                <p className="mt-1 text-sm text-slate-500">Investments will appear here once they are recorded.</p>
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        filteredLedgerInvestments.map((investment) => {
                                                            const eq = Number(investment.equityPercentage || 0);
                                                            return (
                                                                <tr key={investment.id} className={`hover:bg-slate-50/80 transition-colors group ${isNetworkInvestmentRow(investment) ? 'bg-blue-50/40' : ''}`}>
                                                                    <td className="px-6 py-5">
                                                                        <div className="flex items-center gap-3 min-w-0">
                                                                            <div className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700 font-bold text-sm shrink-0">
                                                                                {investment.investorName?.charAt(0) || 'I'}
                                                                            </div>
                                                                            <div className="min-w-0">
                                                                                <p className="text-sm font-bold text-slate-900 truncate flex items-center gap-2">
                                                                                    {investment.investorName}
                                                                                    {isNetworkInvestmentRow(investment) && (
                                                                                        <span className="px-2 py-0.5 text-[9px] font-bold rounded-full bg-blue-100 text-blue-800 uppercase shrink-0">Network</span>
                                                                                    )}
                                                                                </p>
                                                                                <p className="text-[11px] text-slate-500 truncate">{investment.investorEmail}</p>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-5">
                                                                        <p className="text-sm font-bold text-slate-900">
                                                                            {investment.investmentAmount
                                                                                ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: investment.currency || 'INR', maximumFractionDigits: 0 }).format(investment.investmentAmount)
                                                                                : '—'}
                                                                        </p>
                                                                        <p className="text-[11px] text-slate-500 font-medium mt-0.5">{investment.currency || 'INR'}</p>
                                                                    </td>
                                                                    <td className="px-6 py-5">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-sm font-semibold text-slate-900">{investment.equityPercentage != null ? `${eq}%` : '—'}</span>
                                                                            {investment.equityPercentage != null && (
                                                                                <div className="w-12 bg-slate-100 h-1.5 rounded-full overflow-hidden">
                                                                                    <div className="bg-slate-900 h-full rounded-full" style={{ width: `${Math.min(eq, 100)}%` }} />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-5 text-sm font-medium text-slate-800">{investment.numberOfShares ?? '—'}</td>
                                                                    <td className="px-6 py-5 text-sm font-medium text-slate-800">
                                                                        {investment.entrySharePrice != null
                                                                            ? new Intl.NumberFormat('en-IN', { style: 'decimal', minimumFractionDigits: 2 }).format(investment.entrySharePrice)
                                                                            : '—'}
                                                                    </td>
                                                                    <td className="px-6 py-5">
                                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${investmentStatusLedgerClass(investment.status)}`}>
                                                                            {investment.status}
                                                                        </span>
                                                                    </td>
                                                                    {(user?.role === 'ADMIN' || user?.role === 'TENANT_ADMIN' || user?.role === 'INVESTOR_NETWORK' || user?.role === 'MANAGEMENT') && (
                                                                        <td className="px-6 py-5 text-right">
                                                                            {editingFundingId === investment.id ? (
                                                                                <div className="flex flex-wrap items-center justify-end gap-2">
                                                                                    <input
                                                                                        type="number"
                                                                                        min="0"
                                                                                        step="1"
                                                                                        value={editingFundingAmount}
                                                                                        onChange={(e) => setEditingFundingAmount(e.target.value)}
                                                                                        className="w-28 px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900/20"
                                                                                    />
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => updateInvestmentAmount(investment.id, editingFundingAmount)}
                                                                                        disabled={updatingFunding}
                                                                                        className="px-2 py-1.5 text-xs font-bold text-white bg-slate-900 rounded-lg hover:bg-slate-800 disabled:opacity-50"
                                                                                    >
                                                                                        {updatingFunding ? 'Saving…' : 'Save'}
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => { setEditingFundingId(null); setEditingFundingAmount(''); }}
                                                                                        className="px-2 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-lg"
                                                                                    >
                                                                                        Cancel
                                                                                    </button>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="inline-flex items-center justify-end gap-2">
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => { setSelectedInvestment(investment); setShowInvestmentDetailsModal(true); }}
                                                                                        className="px-3 py-1.5 text-xs font-bold text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg border border-blue-200"
                                                                                        title="View investment details"
                                                                                    >
                                                                                        View details
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => { setEditingFundingId(investment.id); setEditingFundingAmount(String(investment.investmentAmount ?? '')); }}
                                                                                        className={`inline-flex items-center gap-1.5 rounded-lg transition-colors ${isPortfolioView
                                                                                            ? 'px-3 py-1.5 text-xs font-bold text-slate-800 bg-slate-100 hover:bg-slate-200 border border-slate-200'
                                                                                            : 'p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100'}`}
                                                                                        title="Edit amount"
                                                                                    >
                                                                                        {isPortfolioView ? (
                                                                                            <>
                                                                                                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                                </svg>
                                                                                                <span>Edit amount</span>
                                                                                            </>
                                                                                        ) : (
                                                                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                                                                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                                                                            </svg>
                                                                                        )}
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        {filteredLedgerInvestments.length > 0 && (
                                            <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-200/80 flex flex-wrap justify-between items-center gap-2">
                                                <p className="text-[11px] text-slate-500 font-semibold">
                                                    Showing {filteredLedgerInvestments.length} of {sortedInvestmentsForNetworkView.length} investors
                                                    {ledgerSearchQ ? ' (filtered)' : ''}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

							{/* Investment Tranches Sub-tab Content */}
							{investmentSubTab === 'tranches' && (
                                <div>
                                    <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
                                        <div>
											<h2 className="text-xl font-extrabold text-slate-900 font-ledger tracking-tight">Investment tranches</h2>
											<p className="text-sm text-slate-500 mt-1">Define network totals, split investor shares, and track milestone terms</p>
                                        </div>
										{canManageTrancheStructure && (
                                            <button
                                                type="button"
                                                onClick={() => { resetRoundForm(); setEditingRound(null); setShowRoundModal(true); }}
                                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-slate-900 to-blue-900 shadow-sm hover:opacity-95 active:scale-[0.98] transition-all"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                                </svg>
												New tranche
                                            </button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
										{/* Tranches List */}
                                        <div className="lg:col-span-1">
                                            <div className="bg-gray-50 rounded-lg border">
                                                <div className="p-4 border-b bg-white rounded-t-lg">
													<h3 className="text-lg font-semibold">Tranches</h3>
                                                </div>
                                                <div className="divide-y max-h-[500px] overflow-y-auto">
                                                    {(Array.isArray(rounds) ? rounds : []).length === 0 ? (
                                                        <div className="p-6 text-center text-gray-500">
                                                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
															<p className="mt-2">No investment tranches</p>
															<p className="text-sm">Click "New Tranche" to create one</p>
                                                        </div>
                                                    ) : (
                                                        (Array.isArray(rounds) ? rounds : []).map(round => (
                                                            <div
                                                                key={round.id}
                                                                onClick={() => { setSelectedRound(round); setSelectedCommitment(null); }}
                                                                className={`p-4 cursor-pointer hover:bg-white ${selectedRound?.id === round.id ? 'bg-blue-50 border-l-4 border-blue-500' : 'bg-white'
                                                                    }`}
                                                            >
                                                                <div className="flex items-center justify-between">
                                                                    <div>
                                                                        <h4 className="font-semibold text-gray-900">{round.roundName}</h4>
                                                                    </div>
                                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoundStatusBadge(round.status)}`}>
                                                                        {round.status}
                                                                    </span>
                                                                </div>
                                                                <div className="mt-2 flex items-center justify-between text-sm">
                                                                    <span className="text-gray-600">
                                                                        {formatCurrency(round.raisedAmount)} / {formatCurrency(round.targetAmount)}
                                                                    </span>
                                                                    <span className="text-gray-500">
                                                                        {round.progressPercentage?.toFixed(0) || 0}%
                                                                    </span>
                                                                </div>
                                                                {round.targetAmount > 0 && (
                                                                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                                                        <div
                                                                            className="bg-blue-600 h-2 rounded-full"
                                                                            style={{ width: `${Math.min(round.progressPercentage || 0, 100)}%` }}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Tranche Details */}
                                        <div className="lg:col-span-2">
                                            {selectedRound ? (
                                                <div className="space-y-6">
                                                    {/* Tranche Info */}
                                                    <div className="bg-gray-50 rounded-lg border p-6">
                                                        <div className="flex items-center justify-between mb-4">
                                                            <h3 className="text-xl font-bold text-gray-900">{selectedRound.roundName}</h3>
                                                            {canManageTrancheStructure && (
                                                                <div className="flex space-x-2">
                                                                    <button
                                                                        onClick={() => openEditRound(selectedRound)}
                                                                        className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded"
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                    {selectedRound.status === 'OPEN' && (
                                                                        <button
                                                                            onClick={() => handleCloseRound(selectedRound)}
                                                                            className="px-3 py-1 text-orange-600 hover:bg-orange-50 rounded"
                                                                        >
                                                                            Close
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        onClick={() => handleDeleteRound(selectedRound)}
                                                                        className="px-3 py-1 text-red-600 hover:bg-red-50 rounded"
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                            <div>
                                                                <p className="text-sm text-gray-500">Target</p>
                                                                <p className="font-semibold">{formatCurrency(selectedRound.targetAmount)}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Raised</p>
                                                                <p className="font-semibold">{formatCurrency(selectedRound.raisedAmount)}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Pre-Money</p>
                                                                <p className="font-semibold">{formatCurrency(selectedRound.preMoneyValuation)}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Post-Money</p>
                                                                <p className="font-semibold">{formatCurrency(selectedRound.postMoneyValuation)}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Share Price</p>
                                                                <p className="font-semibold">{formatCurrency(selectedRound.sharePrice)}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Total Shares</p>
                                                                <p className="font-semibold">{selectedRound.totalShares?.toLocaleString() || '-'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Open Date</p>
                                                                <p className="font-semibold">{selectedRound.openDate || '-'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Close Date</p>
                                                                <p className="font-semibold">{selectedRound.closeDate || '-'}</p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Tranches */}
                                                    <div className="bg-gray-50 rounded-lg border">
                                                        <div className="p-4 border-b bg-white rounded-t-lg flex items-center justify-between">
                                                            <h4 className="text-lg font-semibold">Network Tranches</h4>
                                                            {selectedRound.status === 'OPEN' && canManageTrancheStructure && (
                                                                <button
                                                                    onClick={() => { resetCommitmentForm(); setShowCommitmentModal(true); }}
                                                                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                                                                >
                                                                    Add Tranche
                                                                </button>
                                                            )}
                                                        </div>
                                                        <div className="divide-y">
                                                            {selectedRound.commitments?.length === 0 ? (
                                                                <div className="p-6 text-center text-gray-500">
                                                                    No tranches yet
                                                                </div>
                                                            ) : (
                                                                selectedRound.commitments?.map(commitment => (
                                                                    <div
                                                                        key={commitment.id}
                                                                        onClick={() => setSelectedCommitment(commitment)}
                                                                        className={`p-4 cursor-pointer hover:bg-white ${selectedCommitment?.id === commitment.id ? 'bg-blue-50' : 'bg-white'
                                                                            }`}
                                                                    >
                                                                        <div className="flex items-start justify-between gap-4">
                                                                            <div className="min-w-0">
                                                                                <h5 className="font-semibold">{commitment.networkName}</h5>
                                                                                <p className="text-sm text-gray-500">{commitment.investmentType}</p>
                                                                                {editingCommitmentId !== commitment.id && (commitment.commitmentDate || commitment.commitmentCurrency || commitment.conditions) && (
                                                                                    <div className="mt-2 text-xs text-gray-500 space-y-0.5">
                                                                                        {commitment.commitmentDate ? (
                                                                                            <p>Commitment date: {commitment.commitmentDate}</p>
                                                                                        ) : null}
                                                                                        {commitment.commitmentCurrency ? (
                                                                                            <p>Currency: {commitment.commitmentCurrency}</p>
                                                                                        ) : null}
                                                                                        {commitment.conditions ? (
                                                                                            <p className="whitespace-pre-wrap">Conditions / milestones: {commitment.conditions}</p>
                                                                                        ) : null}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            <div className="text-right shrink-0">
                                                                                {editingCommitmentId === commitment.id ? (
                                                                                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${getRoundStatusBadge(commitment.status)}`}>
                                                                                        {commitment.status}
                                                                                    </span>
                                                                                ) : (
                                                                                    <>
                                                                                        <div className="flex items-center justify-end gap-2">
                                                                                            <p className="font-semibold">{formatCurrency(commitment.committedAmount)}</p>
                                                                                            {canManageTrancheStructure && (
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        setEditingCommitmentId(commitment.id);
                                                                                                        setEditingCommittedAmount(String(commitment.committedAmount ?? ''));
                                                                                                        setEditingCommitmentMeta({
                                                                                                            commitmentDate: commitment.commitmentDate || '',
                                                                                                            commitmentCurrency: commitment.commitmentCurrency || 'INR',
                                                                                                            conditions: commitment.conditions || ''
                                                                                                        });
                                                                                                    }}
                                                                                                    className="px-2 py-0.5 text-xs text-blue-600 hover:bg-blue-50 rounded"
                                                                                                >
                                                                                                    Edit
                                                                                                </button>
                                                                                            )}
                                                                                        </div>
                                                                                        <span className={`mt-1 inline-block px-2 py-0.5 rounded-full text-xs ${getRoundStatusBadge(commitment.status)}`}>
                                                                                            {commitment.status}
                                                                                        </span>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        {editingCommitmentId === commitment.id && (
                                                                            <div
                                                                                className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 border-t border-gray-200 pt-3"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                            >
                                                                                <div>
                                                                                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Total committed</label>
                                                                                    <input
                                                                                        type="number"
                                                                                        min="0"
                                                                                        step="1"
                                                                                        value={editingCommittedAmount}
                                                                                        onChange={(e) => setEditingCommittedAmount(e.target.value)}
                                                                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Commitment date</label>
                                                                                    <input
                                                                                        type="date"
                                                                                        value={editingCommitmentMeta.commitmentDate}
                                                                                        onChange={(e) => setEditingCommitmentMeta((m) => ({ ...m, commitmentDate: e.target.value }))}
                                                                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg"
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Currency</label>
                                                                                    <input
                                                                                        value={editingCommitmentMeta.commitmentCurrency}
                                                                                        onChange={(e) => setEditingCommitmentMeta((m) => ({ ...m, commitmentCurrency: e.target.value }))}
                                                                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg"
                                                                                        placeholder="INR, USD…"
                                                                                    />
                                                                                </div>
                                                                                <div className="sm:col-span-2">
                                                                                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Conditions (milestones / KPIs)</label>
                                                                                    <textarea
                                                                                        rows={2}
                                                                                        value={editingCommitmentMeta.conditions}
                                                                                        onChange={(e) => setEditingCommitmentMeta((m) => ({ ...m, conditions: e.target.value }))}
                                                                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg"
                                                                                    />
                                                                                </div>
                                                                                <div className="sm:col-span-2 flex justify-end gap-2">
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            setEditingCommitmentId(null);
                                                                                            setEditingCommittedAmount('');
                                                                                            setEditingCommitmentMeta({ commitmentDate: '', commitmentCurrency: 'INR', conditions: '' });
                                                                                        }}
                                                                                        className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                                                                                    >
                                                                                        Cancel
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleUpdateCommitmentAmount(commitment);
                                                                                        }}
                                                                                        disabled={updatingCommitmentAmount}
                                                                                        className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                                                                    >
                                                                                        {updatingCommitmentAmount ? 'Saving…' : 'Save commitment'}
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        <div className="mt-2 flex items-center justify-between text-sm text-gray-500">
                                                                            <span>Disbursed: {formatCurrency(commitment.disbursedAmount)}</span>
                                                                            <span>{commitment.disbursementProgress?.toFixed(0) || 0}%</span>
                                                                        </div>
                                                                        <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                                                                            <div
                                                                                className="bg-green-600 h-1.5 rounded-full"
                                                                                style={{ width: `${Math.min(commitment.disbursementProgress || 0, 100)}%` }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Tranche Details */}
                                                    {selectedCommitment && (
                                                        <div className="bg-gray-50 rounded-lg border">
                                                            <div className="p-4 border-b bg-white rounded-t-lg">
                                                                <h4 className="text-lg font-semibold">
                                                                    Tranche Details - {selectedCommitment.networkName}
                                                                </h4>
                                                            </div>

                                                            {/* Participations */}
                                                            <div className="p-4 border-b bg-white">
                                                                <div className="flex items-center justify-between mb-3">
                                                                    <h5 className="font-medium text-gray-700">Investor Participations</h5>
                                                                    {user?.role !== 'INVESTOR' && (
                                                                        <button
                                                                            onClick={() => { resetParticipationForm(); setShowParticipationModal(true); }}
                                                                            className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-sm"
                                                                        >
                                                                            + Add
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                {selectedCommitment.participations?.length === 0 ? (
                                                                    <p className="text-gray-500 text-sm">No participations</p>
                                                                ) : (
                                                                    <div className="space-y-2">
                                                                        {selectedCommitment.participations?.map(p => (
                                                                            <div key={p.id} className="flex items-center justify-between py-2 border-b last:border-0">
                                                                                <div>
                                                                                    <p className="font-medium">{p.investorName}</p>
                                                                                    <p className="text-sm text-gray-500">{p.participationPercentage?.toFixed(2)}% participation</p>
                                                                                </div>
                                                                                <p className="font-semibold">{formatCurrency(p.amount)}</p>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Terms */}
                                                            <div className="p-4 bg-white rounded-b-lg">
                                                                <div className="flex items-center justify-between mb-3">
                                                                    <h5 className="font-medium text-gray-700">Investment Terms (Tranches)</h5>
                                                                    {canAddMilestone && (
                                                                        <button
                                                                            onClick={() => { resetTermForm(); setShowTermModal(true); }}
                                                                            className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-sm"
                                                                        >
                                                                            + Add Term
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                {selectedCommitment.terms?.length === 0 ? (
                                                                    <p className="text-gray-500 text-sm">No terms defined</p>
                                                                ) : (
                                                                    <div className="space-y-2">
                                                                        {selectedCommitment.terms?.map(term => (
                                                                            <div key={term.id} className="flex items-center justify-between py-2 border-b last:border-0">
                                                                                <div>
                                                                                    <p className="font-medium">Term #{term.termNumber}</p>
                                                                                    <p className="text-sm text-gray-500">
                                                                                        Due: {term.dueDate || 'Not set'}
                                                                                        {term.releaseDate ? ` · Release: ${term.releaseDate}` : ''}
                                                                                        {term.milestone && ` — ${term.milestone}`}
                                                                                    </p>
                                                                                </div>
                                                                                <div className="flex items-center space-x-3">
                                                                                    <p className="font-semibold">{formatCurrency(term.amount)}</p>
                                                                                    <span className={`px-2 py-0.5 rounded-full text-xs ${getRoundStatusBadge(term.status)}`}>
                                                                                        {term.status}
                                                                                    </span>
                                                                                    {term.status === 'PENDING' && canUpdateMilestoneStatus && (
                                                                                        <button
                                                                                            onClick={() => handleMarkTermPaid(term)}
                                                                                            className="px-2 py-1 text-green-600 hover:bg-green-50 rounded text-sm"
                                                                                        >
                                                                                            Mark Paid
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="bg-gray-50 rounded-lg border p-12 text-center">
                                                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <h3 className="text-lg font-medium text-gray-900 mb-1">Select a Tranche</h3>
                                                    <p className="text-gray-500">Choose an investment tranche from the list to view details</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Milestones Tab */}
                {activeTab === 'milestones' && (
                    <div className="bg-white rounded-lg shadow">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">Milestones</h2>
                                    <p className="text-sm text-gray-500 mt-1">Milestones defined by investors across all commitments and tranches</p>
                                </div>
                                {canAddMilestone && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const targets = getMilestoneTargets();
                                            if (targets.length === 0) {
                                                setErrorMessage('No tranches available. Create a tranche first.');
                                                setShowErrorDialog(true);
                                                return;
                                            }
                                            if (targets.length === 1) {
                                                const targetId = targets[0].id;
                                                const selectedRoundCandidate = (Array.isArray(rounds) ? rounds : []).find((r) =>
                                                    (r.commitments || []).some((c) => c.id === targetId)
                                                );
                                                const selectedCommitmentCandidate = selectedRoundCandidate?.commitments?.find((c) => c.id === targetId) || null;
                                                setSelectedRound(selectedRoundCandidate || null);
                                                setSelectedCommitment(selectedCommitmentCandidate);
                                                setTermCommitmentIdOverride(targetId);
                                                resetTermForm();
                                                setShowTermModal(true);
                                            } else {
                                                setMilestoneTargetCommitmentId('');
                                                setShowMilestoneTargetModal(true);
                                            }
                                        }}
                                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                                    >
                                        + Add Milestone
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="p-6">
                            {getMilestoneRows().length === 0 ? (
                                <div className="text-center py-12 border border-gray-200 rounded-lg bg-gray-50">
                                    <p className="text-gray-600 font-medium">No milestones found</p>
                                    <p className="text-sm text-gray-500 mt-1">Add investment terms under Investment Tranches to see milestones here.</p>
                                </div>
                            ) : (
                                <div className="overflow-hidden border border-gray-200 rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Milestone</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tranche</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investor Network</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Term</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {getMilestoneRows().map((row) => (
                                                <tr key={row.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{row.milestone}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-700">{row.roundName}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-700">{row.networkName}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-700">Term #{row.termNumber || '-'}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-700">{row.dueDate || 'Not set'}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-700">{formatCurrency(row.amount)}</td>
                                                    <td className="px-6 py-4 text-sm">
                                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoundStatusBadge(row.status)}`}>
                                                            {row.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Stage History Tab */}
                {activeTab === 'stage-history' && (
                    <div className="space-y-6">
                        {/* Stage Graph */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-6">Funding Stage Progression</h2>
                            <div className="relative">
                                {/* Timeline */}
                                <div className="flex justify-between items-center mb-8">
                                    {stageHistory.map((stage, index) => (
                                        <div key={index} className="flex flex-col items-center flex-1">
                                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${index === stageHistory.length - 1
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-green-600 text-white'
                                                }`}>
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <p className="mt-2 text-sm font-medium text-gray-900">{stage.stage}</p>
                                            <p className="text-xs text-gray-500">{stage.date}</p>
                                            {stage.amount && (
                                                <p className="text-sm font-semibold text-green-600 mt-1">{stage.amount}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Connecting Line */}
                                <div className="absolute top-6 left-0 right-0 h-0.5 bg-gray-300" style={{ zIndex: -1 }}></div>
                            </div>
                        </div>

                        {/* Stage Details */}
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount Raised</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {stageHistory.map((stage, index) => (
                                        <tr key={index}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{stage.stage}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{stage.date}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stage.amount || 'N/A'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${index === stageHistory.length - 1
                                                    ? 'bg-blue-100 text-blue-800'
                                                    : 'bg-green-100 text-green-800'
                                                    }`}>
                                                    {index === stageHistory.length - 1 ? 'Current' : 'Completed'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Directors Tab */}
                {activeTab === 'directors' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">Directors & Key Personnel</h2>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {canManageStartupDirectors
                                            ? 'Manage directors, founders, investors, and board members'
                                            : isPortfolioView
                                                ? 'Read-only: director records are maintained by the startup. Contact them for corrections.'
                                                : 'View-only: only the startup account or an administrator can change director records.'}
                                    </p>
                                </div>
                                {canManageStartupDirectors && (
                                    <button
                                        onClick={() => {
                                            setNewDirector({ name: '', email: '', phone: '', role: '', category: '', shareholding: '' });
                                            setShowAddDirectorModal(true);
                                        }}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                        </svg>
                                        <span>Add Director</span>
                                    </button>
                                )}
                            </div>

                            {directors.length === 0 ? (
                                <div className="text-center py-12">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">No directors listed</h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                        {canManageStartupDirectors
                                            ? 'Get started by adding a director or key personnel.'
                                            : 'The startup has not published director records yet.'}
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-hidden border border-gray-200 rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shareholding</th>
                                                {canManageStartupDirectors && (
                                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {directors.map((director) => (
                                                <tr key={director.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-purple-500 to-purple-600 flex items-center justify-center">
                                                                <span className="text-white font-medium">{director.name?.charAt(0) || '?'}</span>
                                                            </div>
                                                            <div className="ml-4">
                                                                <div className="text-sm font-medium text-gray-900">{director.name}</div>
                                                                {director.email && (
                                                                    <div className="text-xs text-gray-500">{director.email}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {director.category ? (
                                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                                                                {director.category.replace(/_/g, ' ')}
                                                            </span>
                                                        ) : (
                                                            <span className="text-sm text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{director.role || '-'}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {director.shareholding ? (
                                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                                {director.shareholding}
                                                            </span>
                                                        ) : (
                                                            <span className="text-sm text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                    {canManageStartupDirectors && (
                                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                            <button
                                                                onClick={() => handleEditDirector(director)}
                                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                                                title="Edit"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setDirectorToDelete(director);
                                                                    setShowDeleteDirectorConfirm(true);
                                                                }}
                                                                className="text-red-600 hover:text-red-900"
                                                                title="Remove"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                </svg>
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            {showEditModal && canEditStartupDetails && (
                <CreateStartupModal
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onSuccess={() => {
                        setShowEditModal(false);
                        loadStartupDetails();
                    }}
                    initialData={startup}
                    isEdit={true}
                />
            )}

            {/* Edit Total Investment Split Modal */}
            {showTotalInvestmentModal && (() => {
                const ledgerModalCurrency = splitEditorInvestments[0]?.currency || 'INR';
                const splitSum = Object.values(splitLedgerDraft || {}).reduce(
                    (sum, row) => sum + (parseFloat(row?.amount) || 0),
                    0
                );
                return (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <h3 className="text-lg font-semibold text-gray-900">Edit Total Investment & Split</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Set the overall total, split amounts per investor, and optional ledger fields:{' '}
                                <strong>share price</strong> (per share), <strong>holding %</strong>, and <strong>number of shares</strong> (CCPS).
                                Network rows are listed first.
                            </p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Total investment amount</label>
                                <div className="flex flex-wrap items-center gap-2">
                                    <input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={totalInvestmentAmountInput}
                                        onChange={(e) => setTotalInvestmentAmountInput(e.target.value)}
                                        className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-500">{ledgerModalCurrency}</span>
                                </div>
                            </div>
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="overflow-x-auto max-h-[min(420px,55vh)] overflow-y-auto">
                                    <table className="min-w-[960px] w-full text-sm border-collapse">
                                        <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                                            <tr className="text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                                <th className="px-4 py-3 border-b border-gray-200 w-[200px]">Investor</th>
                                                <th className="px-3 py-3 border-b border-gray-200 w-[140px]">
                                                    <span className="block normal-case">Committed amount</span>
                                                    <span className="block text-[10px] font-normal text-gray-500 normal-case mt-0.5">({ledgerModalCurrency})</span>
                                                </th>
                                                <th className="px-3 py-3 border-b border-gray-200 w-[150px]">
                                                    <span className="block normal-case">Share price</span>
                                                    <span className="block text-[10px] font-normal text-gray-500 normal-case mt-0.5">Per share (issue / entry)</span>
                                                </th>
                                                <th className="px-3 py-3 border-b border-gray-200 w-[120px]">
                                                    <span className="block normal-case">Holding</span>
                                                    <span className="block text-[10px] font-normal text-gray-500 normal-case mt-0.5">% of company</span>
                                                </th>
                                                <th className="px-3 py-3 border-b border-gray-200 w-[130px]">
                                                    <span className="block normal-case">Shares</span>
                                                    <span className="block text-[10px] font-normal text-gray-500 normal-case mt-0.5">No. of CCPS / units</span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {splitEditorInvestments.map((inv) => {
                                                const isNetworkRow = isNetworkInvestmentRow(inv);
                                                const row = splitLedgerDraft[inv.id] || {};
                                                return (
                                                    <tr key={inv.id} className={isNetworkRow ? 'bg-blue-50/80' : 'bg-white'}>
                                                        <td className="px-4 py-3 align-top min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <p className="text-sm font-medium text-gray-900 truncate">{inv.investorName || 'Investor'}</p>
                                                                {isNetworkRow && (
                                                                    <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-blue-100 text-blue-700 uppercase shrink-0">Network</span>
                                                                )}
                                                            </div>
                                                            <p className="text-xs text-gray-500 truncate mt-0.5">{inv.investorEmail}</p>
                                                        </td>
                                                        <td className="px-3 py-3 align-top">
                                                            <label className="sr-only">Committed amount ({ledgerModalCurrency})</label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="1"
                                                                value={row.amount ?? ''}
                                                                onChange={(e) => mergeSplitLedgerRow(inv.id, { amount: e.target.value })}
                                                                className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-3 align-top">
                                                            <label className="sr-only">Share price per share</label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="any"
                                                                placeholder="e.g. 100"
                                                                title="Price per share at entry (issue price)"
                                                                value={row.entrySharePrice ?? ''}
                                                                onChange={(e) => mergeSplitLedgerRow(inv.id, { entrySharePrice: e.target.value })}
                                                                className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-3 align-top">
                                                            <label className="sr-only">Holding percentage</label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="100"
                                                                step="any"
                                                                placeholder="e.g. 12.5"
                                                                title="Equity / post-money holding %"
                                                                value={row.equityPercentage ?? ''}
                                                                onChange={(e) => mergeSplitLedgerRow(inv.id, { equityPercentage: e.target.value })}
                                                                className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-3 align-top">
                                                            <label className="sr-only">Number of shares</label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="1"
                                                                placeholder="e.g. 10000"
                                                                title="Number of shares or CCPS allotted"
                                                                value={row.numberOfShares ?? ''}
                                                                onChange={(e) => mergeSplitLedgerRow(inv.id, { numberOfShares: e.target.value })}
                                                                className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="text-sm text-gray-600">
                                Split total:{' '}
                                <span className="font-semibold text-gray-900">
                                    {new Intl.NumberFormat('en-IN', {
                                        style: 'currency',
                                        currency: ledgerModalCurrency,
                                        maximumFractionDigits: 0
                                    }).format(splitSum)}
                                </span>
                            </div>
                        </div>
                        <div className="p-6 border-t flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setShowTotalInvestmentModal(false)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={saveTotalInvestmentSplit}
                                disabled={savingTotalInvestmentSplit}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                {savingTotalInvestmentSplit ? 'Saving...' : 'Save Total & Split'}
                            </button>
                        </div>
                    </div>
                </div>
                );
            })()}

            {showInvestmentDetailsModal && selectedInvestment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 z-10">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">Investment Details</h2>
                                    <p className="text-sm text-gray-500 mt-1">{selectedInvestment.investorName || 'Investor'}</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowInvestmentDetailsModal(false);
                                        setSelectedInvestment(null);
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                    aria-label="Close investment details"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                                <div>
                                    <span className="text-gray-500">Amount</span>
                                    <p className="font-semibold text-gray-900">
                                        {selectedInvestment.investmentAmount != null
                                            ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: selectedInvestment.currency || 'INR' }).format(selectedInvestment.investmentAmount)
                                            : '—'}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-gray-500">Date</span>
                                    <p className="font-semibold text-gray-900">
                                        {selectedInvestment.investmentDate ? new Date(selectedInvestment.investmentDate).toLocaleString() : '—'}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-gray-500">Stage</span>
                                    <p className="font-semibold text-gray-900">{selectedInvestment.investmentStage || '—'}</p>
                                </div>
                                <div>
                                    <span className="text-gray-500">Type</span>
                                    <p className="font-semibold text-gray-900">{selectedInvestment.investmentType || '—'}</p>
                                </div>
                            </div>

                            <InvestmentTabbedDetails
                                investment={selectedInvestment}
                                formatMoney={(value, currency = selectedInvestment.currency || 'INR') => {
                                    if (value == null || value === '') return '—';
                                    const n = Number(value);
                                    if (Number.isNaN(n)) return '—';
                                    return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format(n);
                                }}
                                formatDateTime={(value) => (value ? new Date(value).toLocaleString() : '—')}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* File Preview Modal */}
            <FilePreviewModal
                file={previewFile}
                isOpen={showPreviewModal}
                onClose={() => {
                    setShowPreviewModal(false);
                    setPreviewFile(null);
                }}
            />

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Startup?</h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete "{startup.organizationName || startup.name}"? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                                Cancel
                            </button>
                            <button onClick={handleDeleteStartup} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Director Modal */}
            {showAddDirectorModal && canManageStartupDirectors && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Director</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={newDirector.name}
                                    onChange={(e) => setNewDirector({ ...newDirector, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Full name"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                    type="email"
                                    value={newDirector.email}
                                    onChange={(e) => setNewDirector({ ...newDirector, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="email@example.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                <input
                                    type="tel"
                                    value={newDirector.phone}
                                    onChange={(e) => setNewDirector({ ...newDirector, phone: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="+1 234 567 8900"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                                <select
                                    value={newDirector.category}
                                    onChange={(e) => setNewDirector({ ...newDirector, category: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                >
                                    <option value="">Select Category</option>
                                    <option value="PROMOTORS">Promotors</option>
                                    <option value="INVESTORS">Investors</option>
                                    <option value="DIRECTORS">Directors</option>
                                    <option value="BOARD_OBSERVERS">Board Observers</option>
                                    <option value="INDEPENDENT_DIRECTORS">Independent Directors</option>
                                    <option value="FOUNDER_DIRECTORS">Founder Directors</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role/Designation</label>
                                <input
                                    type="text"
                                    value={newDirector.role}
                                    onChange={(e) => setNewDirector({ ...newDirector, role: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="e.g., CEO, CTO, CFO, Co-Founder"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Shareholding</label>
                                <input
                                    type="text"
                                    value={newDirector.shareholding}
                                    onChange={(e) => setNewDirector({ ...newDirector, shareholding: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="e.g., 25% or 1,000,000 shares"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Aadhar Number <span className="text-red-500">*</span>
                                    <span className="text-xs text-gray-500 ml-1">(12 digits)</span>
                                </label>
                                <input
                                    type="text"
                                    value={newDirector.aadharNumber}
                                    onChange={(e) => setNewDirector({ ...newDirector, aadharNumber: e.target.value })}
                                    maxLength="12"
                                    pattern="[0-9]{12}"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="XXXX XXXX XXXX"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    PAN Number <span className="text-red-500">*</span>
                                    <span className="text-xs text-gray-500 ml-1">(10 characters)</span>
                                </label>
                                <input
                                    type="text"
                                    value={newDirector.panNumber}
                                    onChange={(e) => setNewDirector({ ...newDirector, panNumber: e.target.value.toUpperCase() })}
                                    maxLength="10"
                                    pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                                    placeholder="ABCDE1234F"
                                    style={{ textTransform: 'uppercase' }}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowAddDirectorModal(false);
                                    setNewDirector({ name: '', email: '', phone: '', role: '', category: '', shareholding: '', aadharNumber: '', panNumber: '' });
                                }}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddDirector}
                                disabled={!newDirector.name || !newDirector.category}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                Add Director
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Director Modal */}
            {showEditDirectorModal && editingDirector && canManageStartupDirectors && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Director</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={newDirector.name}
                                    onChange={(e) => setNewDirector({ ...newDirector, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Full name"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                    type="email"
                                    value={newDirector.email}
                                    onChange={(e) => setNewDirector({ ...newDirector, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="email@example.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                <input
                                    type="tel"
                                    value={newDirector.phone}
                                    onChange={(e) => setNewDirector({ ...newDirector, phone: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="+1 234 567 8900"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                                <select
                                    value={newDirector.category}
                                    onChange={(e) => setNewDirector({ ...newDirector, category: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                >
                                    <option value="">Select Category</option>
                                    <option value="PROMOTORS">Promotors</option>
                                    <option value="INVESTORS">Investors</option>
                                    <option value="DIRECTORS">Directors</option>
                                    <option value="BOARD_OBSERVERS">Board Observers</option>
                                    <option value="INDEPENDENT_DIRECTORS">Independent Directors</option>
                                    <option value="FOUNDER_DIRECTORS">Founder Directors</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role/Designation</label>
                                <input
                                    type="text"
                                    value={newDirector.role}
                                    onChange={(e) => setNewDirector({ ...newDirector, role: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="e.g., CEO, CTO, CFO, Co-Founder"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Shareholding</label>
                                <input
                                    type="text"
                                    value={newDirector.shareholding}
                                    onChange={(e) => setNewDirector({ ...newDirector, shareholding: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="e.g., 25% or 1,000,000 shares"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Aadhar Number <span className="text-red-500">*</span>
                                    <span className="text-xs text-gray-500 ml-1">(12 digits)</span>
                                </label>
                                <input
                                    type="text"
                                    value={newDirector.aadharNumber}
                                    onChange={(e) => setNewDirector({ ...newDirector, aadharNumber: e.target.value })}
                                    maxLength="12"
                                    pattern="[0-9]{12}"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="XXXX XXXX XXXX"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    PAN Number <span className="text-red-500">*</span>
                                    <span className="text-xs text-gray-500 ml-1">(10 characters)</span>
                                </label>
                                <input
                                    type="text"
                                    value={newDirector.panNumber}
                                    onChange={(e) => setNewDirector({ ...newDirector, panNumber: e.target.value.toUpperCase() })}
                                    maxLength="10"
                                    pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                                    placeholder="ABCDE1234F"
                                    style={{ textTransform: 'uppercase' }}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowEditDirectorModal(false);
                                    setEditingDirector(null);
                                    setNewDirector({ name: '', email: '', phone: '', role: '', category: '', shareholding: '', aadharNumber: '', panNumber: '' });
                                }}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateDirector}
                                disabled={!newDirector.name || !newDirector.category}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                Update Director
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Permissions Modal */}
            {showPermissionsModal && selectedFileForPermissions && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <PermissionsTab
                            file={selectedFileForPermissions}
                            investors={investors}
                            onBack={() => {
                                setShowPermissionsModal(false);
                                setSelectedFileForPermissions(null);
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Investment Tranche Modal */}
            {showRoundModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <h2 className="text-xl font-bold">
                                {editingRound ? 'Edit Investment Tranche' : 'New Investment Tranche'}
                            </h2>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Tranche Name</label>
                                    <select
                                        value={roundForm.roundName}
                                        onChange={(e) => setRoundForm({ ...roundForm, roundName: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    >
                                        <option value="Pre-Seed">Pre-Seed</option>
                                        <option value="Seed">Seed</option>
                                        <option value="Series A">Series A</option>
                                        <option value="Series B">Series B</option>
                                        <option value="Series C">Series C</option>
                                        <option value="Series D">Series D</option>
                                        <option value="Bridge">Bridge</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Amount</label>
                                    <input
                                        type="number"
                                        value={roundForm.targetAmount}
                                        onChange={(e) => setRoundForm({ ...roundForm, targetAmount: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                        placeholder="10000000"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Pre-Money Valuation</label>
                                    <input
                                        type="number"
                                        value={roundForm.preMoneyValuation}
                                        onChange={(e) => setRoundForm({ ...roundForm, preMoneyValuation: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Post-Money Valuation</label>
                                    <input
                                        type="number"
                                        value={roundForm.postMoneyValuation}
                                        onChange={(e) => setRoundForm({ ...roundForm, postMoneyValuation: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Share Price</label>
                                    <input
                                        type="number"
                                        value={roundForm.sharePrice}
                                        onChange={(e) => setRoundForm({ ...roundForm, sharePrice: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Shares</label>
                                    <input
                                        type="number"
                                        value={roundForm.totalShares}
                                        onChange={(e) => setRoundForm({ ...roundForm, totalShares: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Open Date</label>
                                    <input
                                        type="date"
                                        value={roundForm.openDate}
                                        onChange={(e) => setRoundForm({ ...roundForm, openDate: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Close Date</label>
                                    <input
                                        type="date"
                                        value={roundForm.closeDate}
                                        onChange={(e) => setRoundForm({ ...roundForm, closeDate: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                    value={roundForm.notes}
                                    onChange={(e) => setRoundForm({ ...roundForm, notes: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                    rows="3"
                                />
                            </div>

                            {/* Network & investors in this tranche (INVESTOR_NETWORK only, optional) */}
                            {user?.role === 'INVESTOR_NETWORK' && !editingRound && (
                                <div className="border-t border-gray-200 pt-4 mt-4">
                                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Record investments for this tranche (optional)</h4>
                                    <p className="text-xs text-gray-500 mb-3">Add your network&apos;s and members&apos; investment amounts; they will be linked to this tranche.</p>
                                    <div className="space-y-3">
                                        <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-100">
                                            <label className="block text-xs font-medium text-indigo-900 mb-1">Network&apos;s own investment</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                placeholder="Amount"
                                                value={roundInvestments.networkAmount}
                                                onChange={(e) => setRoundInvestments((prev) => ({ ...prev, networkAmount: e.target.value }))}
                                                className="w-full px-3 py-2 border border-indigo-200 rounded-md text-sm"
                                            />
                                        </div>
                                        {(networkInvestors || []).length > 0 && (
                                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                                <label className="block text-xs font-medium text-gray-700 mb-2">Investors in this tranche</label>
                                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                                    {(networkInvestors || []).map((inv) => (
                                                        <div key={inv.id} className="flex items-center gap-2">
                                                            <span className="text-sm text-gray-700 flex-1 truncate">{inv.name || inv.email}</span>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                placeholder="Amount"
                                                                value={roundInvestments.investorAmounts[inv.id] ?? ''}
                                                                onChange={(e) => setRoundInvestments((prev) => ({
                                                                    ...prev,
                                                                    investorAmounts: { ...prev.investorAmounts, [inv.id]: e.target.value }
                                                                }))}
                                                                className="w-28 px-2 py-1.5 border border-gray-300 rounded text-sm"
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="p-6 border-t flex justify-end space-x-3">
                            <button
                                onClick={() => { setShowRoundModal(false); setEditingRound(null); }}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingRound ? handleUpdateRound : handleCreateRound}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                {editingRound ? 'Update' : 'Create'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tranche Modal */}
            {showCommitmentModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <h2 className="text-xl font-bold">Add Network Tranche</h2>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Investor Network</label>
                                <select
                                    value={commitmentForm.networkId}
                                    onChange={(e) => {
                                        const networkId = e.target.value;
                                        setCommitmentForm({ ...commitmentForm, networkId, selectedMemberIds: [] });
                                        loadNetworkMembers(networkId);
                                    }}
                                    className="w-full px-3 py-2 border rounded-lg"
                                >
                                    <option value="">Select Network</option>
                                    {networks.map(n => (
                                        <option key={n.id} value={n.id}>{n.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Network's Own Investment */}
                            {commitmentForm.networkId && (
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-medium text-blue-900">Network's Own Investment</h4>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Network Investment Amount</label>
                                        <input
                                            type="number"
                                            value={commitmentForm.networkInvestmentAmount}
                                            onChange={(e) => setCommitmentForm({ ...commitmentForm, networkInvestmentAmount: e.target.value })}
                                            className="w-full px-3 py-2 border rounded-lg"
                                            placeholder="Amount invested by the network itself"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Member Selection */}
                            {commitmentForm.networkId && networkMembers.length > 0 && (
                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-medium text-gray-900">Select Participating Members</h4>
                                        <div className="flex space-x-2">
                                            <button
                                                type="button"
                                                onClick={() => setCommitmentForm({ ...commitmentForm, selectedMemberIds: networkMembers.map(m => m.id) })}
                                                className="text-xs text-blue-600 hover:underline"
                                            >
                                                Select All
                                            </button>
                                            <span className="text-gray-300">|</span>
                                            <button
                                                type="button"
                                                onClick={() => setCommitmentForm({ ...commitmentForm, selectedMemberIds: [] })}
                                                className="text-xs text-gray-600 hover:underline"
                                            >
                                                Clear All
                                            </button>
                                        </div>
                                    </div>
                                    <div className="max-h-48 overflow-y-auto space-y-2">
                                        {networkMembers.map(member => (
                                            <label key={member.id} className="flex items-center p-2 bg-white rounded border hover:bg-gray-50 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={commitmentForm.selectedMemberIds.includes(member.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setCommitmentForm({
                                                                ...commitmentForm,
                                                                selectedMemberIds: [...commitmentForm.selectedMemberIds, member.id]
                                                            });
                                                        } else {
                                                            setCommitmentForm({
                                                                ...commitmentForm,
                                                                selectedMemberIds: commitmentForm.selectedMemberIds.filter(id => id !== member.id)
                                                            });
                                                        }
                                                    }}
                                                    className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                                />
                                                <div className="ml-3 flex-1">
                                                    <p className="text-sm font-medium text-gray-900">{member.name}</p>
                                                    <p className="text-xs text-gray-500">{member.email}</p>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                    {commitmentForm.selectedMemberIds.length > 0 && (
                                        <p className="mt-2 text-sm text-gray-600">
                                            {commitmentForm.selectedMemberIds.length} member(s) selected
                                        </p>
                                    )}
                                </div>
                            )}

                            {commitmentForm.networkId && networkMembers.length === 0 && (
                                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                                    <p className="text-sm text-yellow-800">This network has no members yet. Only the network itself can invest.</p>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Committed Amount</label>
                                    <input
                                        type="number"
                                        value={commitmentForm.committedAmount}
                                        onChange={(e) => setCommitmentForm({ ...commitmentForm, committedAmount: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Investment Type</label>
                                    <select
                                        value={commitmentForm.investmentType}
                                        onChange={(e) => setCommitmentForm({ ...commitmentForm, investmentType: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    >
                                        <option value="EQUITY">Equity</option>
                                        <option value="DEBT">Debt</option>
                                        <option value="CCPS">CCPS</option>
                                        <option value="CCD">CCD</option>
                                        <option value="SAFE">SAFE</option>
                                        <option value="CONVERTIBLE_NOTE">Convertible Note</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Commitment date</label>
                                    <input
                                        type="date"
                                        value={commitmentForm.commitmentDate}
                                        onChange={(e) => setCommitmentForm({ ...commitmentForm, commitmentDate: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                                    <input
                                        value={commitmentForm.commitmentCurrency}
                                        onChange={(e) => setCommitmentForm({ ...commitmentForm, commitmentCurrency: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                        placeholder="INR, USD…"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Conditions (milestones, KPIs)</label>
                                <textarea
                                    rows={2}
                                    value={commitmentForm.conditions}
                                    onChange={(e) => setCommitmentForm({ ...commitmentForm, conditions: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                    placeholder="e.g. Release next tranche after MRR target…"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Equity %</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={commitmentForm.equityPercentage}
                                        onChange={(e) => setCommitmentForm({ ...commitmentForm, equityPercentage: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of Shares</label>
                                    <input
                                        type="number"
                                        value={commitmentForm.numberOfShares}
                                        onChange={(e) => setCommitmentForm({ ...commitmentForm, numberOfShares: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                    value={commitmentForm.notes}
                                    onChange={(e) => setCommitmentForm({ ...commitmentForm, notes: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                    rows="2"
                                />
                            </div>
                        </div>
                        <div className="p-6 border-t flex justify-end space-x-3">
                            <button
                                onClick={() => setShowCommitmentModal(false)}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateCommitment}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Add Tranche
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Participation Modal */}
            {showParticipationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="p-6 border-b">
                            <h2 className="text-xl font-bold">Add Investor Participation</h2>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Investor</label>
                                <select
                                    value={participationForm.investorId}
                                    onChange={(e) => setParticipationForm({ ...participationForm, investorId: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                >
                                    <option value="">Select Investor</option>
                                    {allInvestors.map(i => (
                                        <option key={i.id} value={i.id}>{i.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                                    <input
                                        type="number"
                                        value={participationForm.amount}
                                        onChange={(e) => setParticipationForm({ ...participationForm, amount: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Participation %</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={participationForm.participationPercentage}
                                        onChange={(e) => setParticipationForm({ ...participationForm, participationPercentage: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Shares Allocated</label>
                                <input
                                    type="number"
                                    value={participationForm.sharesAllocated}
                                    onChange={(e) => setParticipationForm({ ...participationForm, sharesAllocated: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                    value={participationForm.notes}
                                    onChange={(e) => setParticipationForm({ ...participationForm, notes: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                    rows="2"
                                />
                            </div>
                        </div>
                        <div className="p-6 border-t flex justify-end space-x-3">
                            <button
                                onClick={() => setShowParticipationModal(false)}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateParticipation}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Add Participation
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Term Modal */}
            {showTermModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="p-6 border-b">
                            <h2 className="text-xl font-bold">Add Investment Term</h2>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                                    <input
                                        type="number"
                                        value={termForm.amount}
                                        onChange={(e) => setTermForm({ ...termForm, amount: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Percentage %</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={termForm.percentage}
                                        onChange={(e) => setTermForm({ ...termForm, percentage: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Due date</label>
                                    <input
                                        type="date"
                                        value={termForm.dueDate}
                                        onChange={(e) => setTermForm({ ...termForm, dueDate: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Release date (tranche)</label>
                                    <input
                                        type="date"
                                        value={termForm.releaseDate}
                                        onChange={(e) => setTermForm({ ...termForm, releaseDate: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Milestone condition</label>
                                <input
                                    type="text"
                                    value={termForm.milestone}
                                    onChange={(e) => setTermForm({ ...termForm, milestone: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                    placeholder="e.g., Product launch, Revenue target"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                    value={termForm.notes}
                                    onChange={(e) => setTermForm({ ...termForm, notes: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                    rows="2"
                                />
                            </div>
                        </div>
                        <div className="p-6 border-t flex justify-end space-x-3">
                            <button
                                onClick={() => { setTermCommitmentIdOverride(null); setShowTermModal(false); }}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateTerm}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Add Term
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Milestone Target Modal */}
            {showMilestoneTargetModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="p-6 border-b">
                            <h2 className="text-xl font-bold">Select Tranche</h2>
                        </div>
                        <div className="p-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Tranche</label>
                            <select
                                value={milestoneTargetCommitmentId}
                                onChange={(e) => setMilestoneTargetCommitmentId(e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg"
                            >
                                <option value="">Select tranche</option>
                                {getMilestoneTargets().map((target) => (
                                    <option key={target.id} value={target.id}>{target.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="p-6 border-t flex justify-end gap-3">
                            <button
                                onClick={() => setShowMilestoneTargetModal(false)}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    const targetId = Number(milestoneTargetCommitmentId);
                                    if (!targetId) return;
                                    const selectedRoundCandidate = (Array.isArray(rounds) ? rounds : []).find((r) =>
                                        (r.commitments || []).some((c) => c.id === targetId)
                                    );
                                    const selectedCommitmentCandidate = selectedRoundCandidate?.commitments?.find((c) => c.id === targetId) || null;
                                    setSelectedRound(selectedRoundCandidate || null);
                                    setSelectedCommitment(selectedCommitmentCandidate);
                                    setTermCommitmentIdOverride(targetId);
                                    resetTermForm();
                                    setShowMilestoneTargetModal(false);
                                    setShowTermModal(true);
                                }}
                                disabled={!milestoneTargetCommitmentId}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Dialog */}
            <CustomDialog
                isOpen={showErrorDialog}
                onClose={() => setShowErrorDialog(false)}
                title="Update Failed"
                message={errorMessage}
                type="error"
                confirmText="OK"
            />

            {/* Success Dialog */}
            <CustomDialog
                isOpen={showSuccessDialog}
                onClose={() => setShowSuccessDialog(false)}
                title="Success"
                message={successMessage}
                type="success"
                confirmText="OK"
            />

            {/* Delete Director Confirmation */}
            <ConfirmDialog
                isOpen={showDeleteDirectorConfirm && canManageStartupDirectors}
                onClose={() => {
                    setShowDeleteDirectorConfirm(false);
                    setDirectorToDelete(null);
                }}
                onConfirm={() => {
                    if (directorToDelete) {
                        handleDeleteDirector(directorToDelete.id);
                        setShowDeleteDirectorConfirm(false);
                        setDirectorToDelete(null);
                    }
                }}
                title="Delete Director"
                message={`Are you sure you want to delete ${directorToDelete?.name}? This action cannot be undone.`}
            />
            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={confirm.isOpen}
                title={confirm.title}
                message={confirm.message}
                type={confirm.type}
                onConfirm={confirm.onConfirm}
                onClose={() => setConfirm({ ...confirm, isOpen: false })}
            />
        </>
    );

    if (user?.role === 'INVESTOR_NETWORK' || user?.role === 'MANAGEMENT') {
        const sidebarOpen = sidebarExpanded;
        const navItems = [
            { id: 'portfolio', label: 'Portfolio', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', path: `/${tenantPath}/investor-network` },
            { id: 'startups', label: 'Startups', icon: 'M13 10V3L4 14h7v7l9-11h-7z', path: `/${tenantPath}/investor-network/startups` },
            { id: 'investors', label: 'Investors', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', path: `/${tenantPath}/investor-network/investors` },
            { id: 'users', label: 'Management', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', path: `/${tenantPath}/investor-network/users` },
            { id: 'documents', label: 'Documents', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', path: `/${tenantPath}/investor-network/documents` },
            { id: 'activity', label: 'Activity', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', path: `/${tenantPath}/investor-network/activity` }
        ];
        return (
            <div className="min-h-screen bg-slate-50/80 flex">
                <aside
                    className={`fixed left-0 top-0 h-full z-40 flex flex-col bg-white/95 backdrop-blur-sm border-r border-slate-200/80 shadow-sm transition-[width] duration-200 ease-out ${sidebarOpen ? 'w-52' : 'w-[72px]'}`}
                >
                    <div className="h-14 flex items-center shrink-0 px-3 border-b border-slate-100">
                        {sidebarOpen ? (
                            <Logo width={36} height={36} showText={true} className="[&_span]:!text-lg" />
                        ) : (
                            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                                <span className="text-white font-bold text-sm">i</span>
                            </div>
                        )}
                    </div>
                    <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
                        {navItems.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => navigate(item.path)}
                                title={item.label}
                                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all duration-150 ${item.id === 'startups'
                                    ? 'bg-indigo-50 text-indigo-700 font-medium shadow-sm'
                                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                    }`}
                            >
                                <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                                </svg>
                                {sidebarOpen && <span className="text-sm truncate">{item.label}</span>}
                            </button>
                        ))}
                    </nav>
                    <div className="shrink-0 px-2 py-1 border-t border-slate-100">
                        <button
                            type="button"
                            onClick={() => setSidebarExpanded((e) => !e)}
                            className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
                            title={sidebarExpanded ? 'Collapse menu' : 'Expand menu'}
                        >
                            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={sidebarExpanded ? 'M11 19l-7-7 7-7m8 14l-7-7 7-7' : 'M13 5l7 7-7 7M5 5l7 7-7 7'} />
                            </svg>
                            {sidebarOpen && <span className="text-xs font-medium">{sidebarExpanded ? 'Collapse' : 'Expand'}</span>}
                        </button>
                    </div>
                    <div className="shrink-0 p-2 border-t border-slate-100 bg-slate-50/50">
                        <div className="flex items-center gap-2">
                            <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                                <span className="text-white font-bold text-sm">
                                    {(user?.organizationName || user?.name || 'N')?.charAt(0)?.toUpperCase()}
                                </span>
                            </div>
                            {sidebarOpen && (
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-slate-900 truncate">{user?.organizationName || user?.name}</p>
                                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </aside>
                <main className={`flex-1 min-h-screen overflow-auto transition-[margin] duration-200 ease-out ${sidebarOpen ? 'ml-52' : 'ml-[72px]'}`}>
                    {mainContent}
                </main>
            </div>
        );
    }

    return mainContent;
};

export default StartupDetail;
